// Authenticated fetch helper. Every API call goes through here so the
// access token always rides along.

import type { AnalysisRun } from "./analysisGate";
import type { TagView } from "./tags";
import { getActiveDatabase, getPreferredLanguage } from "./preferences";
import { getSessionId } from "./session";
import { describeApiError } from "./apiErrors";

/** A refused or failed call. `error` is the server's code, kept for the code
 *  that branches on it; `message` is the sentence the user reads. */
export interface ApiFailure {
  ok: false;
  status: number;
  error: string;
  detail?: string;
  message: string;
}

export type ApiResult<T> = { ok: true; data: T; warnings?: string[] } | ApiFailure;

/** Builds the one failure shape every call returns, with its plain sentence. */
export function apiFailure(
  status: number,
  error: string | undefined,
  detail?: string,
): ApiFailure {
  const out: ApiFailure = {
    ok: false,
    status,
    error: error ?? `HTTP ${status}`,
    message: describeApiError(status, error, detail),
  };
  if (detail !== undefined) out.detail = detail;
  return out;
}

interface ApiErrorBody {
  error?: string;
  detail?: string;
}

/**
 * Subscriber called when the server tells us this session has been
 * superseded by a sign-in elsewhere (or that we never had a valid
 * session_id to begin with). Set by `SessionWatcher` at app boot so
 * the lower-level apiFetch can signal back without taking a circular
 * dependency on the React tree.
 */
type SessionForcedSignOutReason = "session_superseded" | "session_required";
let sessionForcedSignOutListener:
  ((reason: SessionForcedSignOutReason) => void) | null = null;
export function setSessionForcedSignOutListener(
  fn: ((reason: SessionForcedSignOutReason) => void) | null,
) {
  sessionForcedSignOutListener = fn;
}

const SESSION_HEADER = "X-Session-Id";

/** Headers for the rare authenticated fetch that cannot go through
 *  `apiFetch` (raw CSV/text downloads, non-JSON responses). Includes the
 *  single-session header — the one call that omitted it (audit CSV export)
 *  401'd on every OIDC deployment and had never worked. */
export function authHeaders(
  accessToken: string | undefined,
): Record<string, string> {
  const sessionId = getSessionId();
  return {
    // Omitted entirely when absent — see the note on `apiFetch`. `Bearer
    // undefined` is a credential the server must reject, which is a 401; no
    // header at all is a guest request.
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
    ...(getActiveDatabase() ? { [DATABASE_HEADER]: getActiveDatabase() as string } : {}),
  };
}
/** The database a request addresses; absent, the site's default. */
export const DATABASE_HEADER = "X-Tres-Database";

const SESSION_FORCED_ERRORS = new Set([
  "session_superseded",
  "session_required",
]);

/** Reads a non-ok response into the failure shape. A 401 naming a session
 *  that ended elsewhere also tells the SPA to send the user back to sign-in. */
export async function failureFromResponse(res: Response): Promise<ApiFailure> {
  let body: ApiErrorBody = {};
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    // Non-JSON error body (rate-limit middleware, ALB): the status speaks.
  }
  if (res.status === 401 && body.error && SESSION_FORCED_ERRORS.has(body.error)) {
    sessionForcedSignOutListener?.(body.error as SessionForcedSignOutReason);
  }
  return apiFailure(res.status, body.error, body.detail);
}

// 503 retry policy.
//
// The cloud preview's ECS service runs `deployment_minimum_healthy_percent = 0`
// because the EFS-shared TDB2 lock can't tolerate two simultaneous tasks
// (see terraform/aws/cloud-preview/ecs.tf). That means every deploy
// has a 10-30s window where the ALB has no healthy targets and
// returns 503 directly. Issue #47 was a real user hitting one such
// window.
//
// Retry only on 503 — other statuses have meaningful semantics we
// shouldn't mask (502 may indicate a target-group health issue, 429
// is rate-limit and self-heals on a different timeline, 5xx generally
// is a code bug we want surfaced). Total max wait ≈ 6s of backoff +
// real response time. Logged to console so a developer watching the
// devtools sees what happened, but no UI surfacing — the retries are
// fast enough to be invisible in the success case.
const RETRY_503_DELAYS_MS = [2000, 4000];

async function fetchWithRetry503(
  input: RequestInfo,
  init?: RequestInit,
): Promise<Response> {
  // Every call into the API names the database it addresses, the raw
  // downloads and uploads that build their own headers included (36.12).
  const activeDatabase = getActiveDatabase();
  if (activeDatabase && typeof input === "string" && input.startsWith("/api/")) {
    const headers = new Headers(init?.headers ?? {});
    if (!headers.has(DATABASE_HEADER)) headers.set(DATABASE_HEADER, activeDatabase);
    init = { ...(init ?? {}), headers };
  }
  for (let attempt = 0; ; attempt++) {
    const res = await fetch(input, init);
    if (res.status !== 503 || attempt >= RETRY_503_DELAYS_MS.length) {
      return res;
    }
    const delay = RETRY_503_DELAYS_MS[attempt] ?? 2000;
    const url = typeof input === "string" ? input : input.url;
    console.warn(
      `[api] 503 on ${url}; retrying in ${delay / 1000}s ` +
        `(attempt ${attempt + 2} of ${RETRY_503_DELAYS_MS.length + 1})`,
    );
    await new Promise((r) => setTimeout(r, delay));
  }
}

export async function apiFetch<T>(
  path: string,
  // Undefined on instances that serve signed-out visitors: the request goes
  // out with NO Authorization header, and the API resolves it to the
  // read-only anonymous role. Sending `Bearer undefined` instead would be a
  // credential the server must try to verify and reject, which is a 401, not
  // a guest session.
  accessToken: string | undefined,
  init: RequestInit = {},
): Promise<ApiResult<T>> {
  const sessionId = getSessionId();
  const preferredLang = getPreferredLanguage();
  const res = await fetchWithRetry503(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      // Single-session enforcement: server compares this against
      // its per-user active session_id. Mismatch → 401
      // session_superseded. Missing → 401 session_required.
      ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
      // Honor the user's Preferences-page language pick for label
      // resolution server-side. Omitted when nothing is set so the
      // backend falls back to English-first.
      ...(preferredLang ? { "Accept-Language": preferredLang } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) return failureFromResponse(res);

  // 204 No Content: empty body, no JSON to parse. The DELETE
  // endpoints (vocab concept, future) return this; calling
  // `res.json()` on an empty body throws SyntaxError and the caller
  // never sees `ok: true`. Caller should declare the result type
  // as `void` / `null` when hitting a 204 endpoint.
  if (res.status === 204) {
    return { ok: true, data: null as T };
  }

  // SPARQL JSON Results passes through tres-api unchanged. Caller
  // narrows the shape via the generic parameter.
  const data = (await res.json()) as T;
  // Non-blocking findings arrive two ways and merge into one banner: the
  // RDF validator's ride a header (handlers/query.rs), the QQL engine's
  // advisories (e.g. an undeclared prefix kept verbatim, 6.6) ride the
  // result envelope.
  const warningsHeader = res.headers.get("X-Tres-Query-Warnings");
  const headerWarnings = warningsHeader ? warningsHeader.split(" | ") : [];
  const envelope = data as { warnings?: unknown };
  const bodyWarnings = Array.isArray(envelope?.warnings)
    ? envelope.warnings.filter((w): w is string => typeof w === "string")
    : [];
  const warnings = [...headerWarnings, ...bodyWarnings];
  if (warnings.length > 0) {
    return { ok: true, data, warnings };
  }
  return { ok: true, data };
}

// ---- Query log (6.3/6.4) — the bodies, split from the audit trail ----

export interface QueryLogEntry {
  id: number;
  ran_at: string;
  actor: string;
  kind: "query" | "llm-query" | "llm-question";
  body: string;
  graph?: string;
  answer_id?: string;
  duration_ms?: number;
  row_count?: number;
  ok: boolean;
}

export function listQueryLog(
  idToken: string,
  answer?: string,
): Promise<ApiResult<{ entries: QueryLogEntry[] }>> {
  const suffix = answer ? `?answer=${encodeURIComponent(answer)}` : "";
  return apiFetch(`/api/v1/admin/query-log${suffix}`, idToken);
}

export interface QueryLogGrant {
  enabled: boolean;
  granted_by?: string;
  granted_at?: string;
}

export function queryLogGrant(
  idToken: string,
): Promise<ApiResult<QueryLogGrant>> {
  return apiFetch("/api/v1/admin/query-log-access", idToken);
}

export function setQueryLogGrant(
  enabled: boolean,
  idToken: string,
): Promise<ApiResult<null>> {
  return apiFetch("/api/v1/admin/query-log-access", idToken, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
  });
}

// Convenience wrapper specifically for /api/v1/query, the only authenticated
// endpoint shipping in this preview phase.
export interface SparqlQueryRequest {
  query: string;
  accept?: string;
  /** Graph scope. Each entry attaches as SPARQL-protocol dataset params
   *  (`default-graph-uri` + `named-graph-uri`, the desktop's SELECT
   *  semantics) so the query reads from that set without the user writing
   *  FROM/GRAPH clauses (issue #55; multi-graph SELECT). Empty/omitted =
   *  the endpoint's default dataset. Explicit `GRAPH <...>` in the query
   *  body still takes precedence per the spec. */
  graphs?: string[];
}

export interface SparqlBinding {
  type: "uri" | "literal" | "bnode" | "typed-literal";
  value: string;
  "xml:lang"?: string;
  datatype?: string;
}

/** The support set of a scope/temporal-aware answer (QROS): the in-scope
 *  denominator the answer ranges over — "for the N statements in scope (as
 *  of T), the answer is Y" instead of a bare value. */
export interface QuerySupportInfo {
  asOf: string | null;
  statementCount: number;
  subjects: string[];
  subjectsTruncated: boolean;
}

/** One statement a result row matched, with every tag it carries (QQL TAG-8). */
export interface RowStatement {
  predicate: string;
  predicateLabel: string;
  tags: TagView[];
}

/** The statements one result row matched; `more` counts those not read. */
export interface RowStatements {
  statements: RowStatement[];
  more: number;
}

export interface SparqlSelectResult {
  head: { vars: string[] };
  results: { bindings: Array<Record<string, SparqlBinding>> };
  support?: QuerySupportInfo;
  /** Parallel to the bindings, on QROS: each row's statements and tags. */
  rowStatements?: RowStatements[];
}

export interface SparqlAskResult {
  head: Record<string, never>;
  boolean: boolean;
}

export type SparqlResult = SparqlSelectResult | SparqlAskResult;

export function isSelectResult(r: SparqlResult): r is SparqlSelectResult {
  return "results" in r;
}

export function runQuery(
  body: SparqlQueryRequest,
  accessToken: string | undefined,
  signal?: AbortSignal,
): Promise<ApiResult<SparqlResult>> {
  return apiFetch<SparqlResult>("/api/v1/query", accessToken, {
    method: "POST",
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
}

// --- AI chat (LLM Testbed) ---

export interface AiChatRequest {
  message: string;
  /** Optional default-graph URI, same semantic as the Query
   *  endpoint's `graph` field. The handler forwards it to Fuseki
   *  as `default-graph-uri` so tool-issued SPARQL reads from the
   *  picked graph (issue #55, graph-selector contract). */
  graph?: string;
}

/** A trace step in an AI chat response. Discriminated by `kind`. */
export type AiChatTraceStep =
  | { kind: "generated_query"; sparql: string }
  | {
      kind: "query_result";
      rows: Array<Record<string, string>>;
      truncated: boolean;
    }
  | { kind: "query_error"; sparql: string; message: string }
  | { kind: "tool_result"; tool: string; text: string };

export interface AiChatTokenUsage {
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
}

/** One model call's cost within a turn (list 79.3). */
export interface AiChatTripUsage {
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens: number;
  cache_creation_input_tokens: number;
  tool_calls: number;
  kind: string;
}

export interface AiChatResponse {
  /** Final assistant answer text. May be empty when the model hit
   *  the round-trip cap; `stop_reason` is then `max_roundtrips`. */
  answer: string;
  /** Ordered list of GeneratedQuery + QueryResult/QueryError steps
   *  emitted by the tool-call loop. Empty when the model answered
   *  without using the tool. */
  trace: AiChatTraceStep[];
  latency_ms: number;
  /** Token usage aggregated across LLM round-trips. Undefined when
   *  the provider didn't report usage (some Ollama models). */
  usage?: AiChatTokenUsage;
  /** `stop` (natural), `max_tokens` (LLM hit cap mid-answer),
   *  `max_roundtrips` (handler stopped after the tool-call cap),
   *  `token_budget` (handler stopped at the turn's token budget),
   *  `other` (provider-specific). */
  stop_reason: string;
  /** Model calls the turn made, and each call's cost in order. */
  llm_calls: number;
  trips: AiChatTripUsage[];
  /** The limits the turn ran under; a cutoff was measured against these. */
  turn_token_budget?: number;
  max_roundtrips: number;
  /** Row id of the persisted quick-question record, for in-place grading.
   *  Absent when the deployment has no state store. */
  question_id?: string;
}

/** Ask the same question with `stream: true`, calling `onProgress` with one
 *  short plain-language line per model round and per tool call. A grounded
 *  answer can take minutes of retrieval; without this the surface showed a
 *  motionless "Thinking…" and looked hung. The final NDJSON line carries the
 *  response object, and a provider failure carries the status a plain
 *  request would have returned.
 *
 *  This is the only chat entry point. The non-streaming `aiChat` it replaced
 *  was removed on 2026-08-21 once it had no caller left — a second path to
 *  the same endpoint is a second set of behaviours to keep true. */
export async function aiChatStreaming(
  body: AiChatRequest,
  accessToken: string | undefined,
  onProgress: (text: string) => void,
): Promise<ApiResult<AiChatResponse>> {
  const preferredLang = getPreferredLanguage();
  const res = await fetchWithRetry503("/api/v1/ai/chat", {
    method: "POST",
    body: JSON.stringify({ ...body, stream: true }),
    headers: {
      "Content-Type": "application/json",
      Accept: "application/x-ndjson",
      ...authHeaders(accessToken),
      ...(preferredLang ? { "Accept-Language": preferredLang } : {}),
    },
  });

  // Refusals (403, 429 budget/concurrency, 503 no provider) come back as the
  // ordinary JSON error body — the stream only begins once the turn is
  // running, so this is the same handling apiFetch does.
  if (!res.ok) return failureFromResponse(res);

  let result: ApiResult<AiChatResponse> | null = null;
  const consume = (line: string) => {
    const text = line.trim();
    if (!text) return;
    let event: {
      event?: string;
      text?: string;
      response?: AiChatResponse;
      status?: number;
      error?: string;
      detail?: string;
    };
    try {
      event = JSON.parse(text) as typeof event;
    } catch {
      return;
    }
    if (event.event === "progress" && event.text) {
      onProgress(event.text);
    } else if (event.event === "done" && event.response) {
      result = { ok: true, data: event.response };
    } else if (event.event === "error") {
      result = apiFailure(
        event.status ?? 502,
        event.error ?? "The assistant did not finish the answer",
        event.detail,
      );
    }
  };

  const reader = res.body?.getReader();
  if (reader) {
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl = buffer.indexOf("\n");
      while (nl >= 0) {
        consume(buffer.slice(0, nl));
        buffer = buffer.slice(nl + 1);
        nl = buffer.indexOf("\n");
      }
    }
    consume(buffer);
  } else {
    // No streaming body (a runtime without ReadableStream): the lines are all
    // there, just at once — the answer still arrives, only the progress is
    // retrospective.
    (await res.text()).split("\n").forEach(consume);
  }

  return (
    result ??
    apiFailure(
      502,
      "The answer stream ended before the answer arrived. Ask again; if it repeats, the connection is being cut mid-answer",
    )
  );
}

// --- Trainer Evaluator ---
//
// Tenant-scoped question banks, runs against the configured LLM,
// human grading. See DATA_PREPAREDNESS_EVAL_DESIGN.md and
// crates/tres-api/src/handlers/evaluator.rs.

export type QuestionType = "ontology" | "data";

export interface BankSummary {
  id: string;
  name: string;
  description: string;
  question_count: number;
  created_at: string;
  updated_at: string;
}

export interface EvalQuestion {
  id: string;
  type: QuestionType;
  text: string;
  expected_answer: string;
  /** True when the correct behavior is a decline — the hallucination check. */
  unanswerable: boolean;
}

export interface BankDetail {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  questions: EvalQuestion[];
}

// 'A' = no schema injection, 'B' = schema-context injected, 'O' = ontology-only
// (deterministic resolve+retrieve, no LLM call / no cost).
export type EvalMode = "A" | "B" | "O";
export type RunStatus = "running" | "completed" | "failed" | "cancelled";
export type HumanGrade = "pass" | "fail" | "partial";

export interface RunListRow {
  id: string;
  model_provider: string;
  model_name: string;
  mode: EvalMode;
  status: RunStatus;
  started_at: string;
  completed_at: string | null;
  run_by: string | null;
  /** `run_by` resolved to an email for display; absent when the server
   *  cannot resolve it. */
  run_by_email?: string | null;
}

export interface RunSummary {
  total: number;
  completed: number;
  pass: number;
  fail: number;
  partial: number;
  ungraded: number;
  errored: number;
}

export interface RunResultRow {
  id: string;
  /** Null when the question was later removed from the bank; the snapshot
   *  fields below still carry what was asked. */
  question_id: string | null;
  /** Why the turn ended: "stop", "max_tokens", "max_roundtrips", … Null on
   *  rows recorded before stop reasons were captured. */
  stop_reason: string | null;
  question_text: string;
  question_type: QuestionType;
  expected_answer: string;
  ordering: number;
  generated_sparql: string | null;
  /** JSON-encoded `AiChatTraceStep[]`. Parse client-side. */
  trace_json: string;
  answer_text: string;
  latency_ms: number;
  input_tokens: number;
  output_tokens: number;
  error_message: string | null;
  human_grade: HumanGrade | null;
  human_grade_notes: string | null;
  criteria_scores: string | null;
  graded_by: string | null;
  graded_at: string | null;
  unanswerable: boolean;
  structured_answer: string | null;
  /** Model calls the turn made. Null on rows recorded before list 79.3,
   *  as are the three fields below. */
  llm_calls: number | null;
  cache_read_tokens: number | null;
  cache_write_tokens: number | null;
  /** The per-trip ledger and the limits that applied, JSON-encoded; read by
   *  `TokenomicsPanel`. */
  tokenomics_json: string | null;
}

export interface RunDetail {
  id: string;
  bank_id: string;
  model_provider: string;
  model_name: string;
  mode: EvalMode;
  status: RunStatus;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  graph: string | null;
  results: RunResultRow[];
  summary: RunSummary;
}

export function listEvalBanks(
  accessToken: string,
): Promise<ApiResult<{ banks: BankSummary[] }>> {
  return apiFetch("/api/v1/eval/banks", accessToken);
}

export function createEvalBank(
  body: { name: string; description?: string },
  accessToken: string,
): Promise<ApiResult<{ id: string; name: string; description: string }>> {
  return apiFetch("/api/v1/eval/banks", accessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getEvalBank(
  id: string,
  accessToken: string,
): Promise<ApiResult<BankDetail>> {
  return apiFetch(`/api/v1/eval/banks/${encodeURIComponent(id)}`, accessToken);
}

export function updateEvalBank(
  id: string,
  body: { name: string; description?: string },
  accessToken: string,
): Promise<ApiResult<null>> {
  return apiFetch(`/api/v1/eval/banks/${encodeURIComponent(id)}`, accessToken, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function deleteEvalBank(
  id: string,
  accessToken: string,
): Promise<ApiResult<null>> {
  return apiFetch(`/api/v1/eval/banks/${encodeURIComponent(id)}`, accessToken, {
    method: "DELETE",
  });
}

export function replaceEvalQuestions(
  bankId: string,
  questions: Array<{
    id?: string;
    type: QuestionType;
    text: string;
    expected_answer?: string;
    unanswerable?: boolean;
  }>,
  accessToken: string,
): Promise<ApiResult<{ count: number }>> {
  return apiFetch(
    `/api/v1/eval/banks/${encodeURIComponent(bankId)}/questions`,
    accessToken,
    {
      method: "PUT",
      body: JSON.stringify({ questions }),
    },
  );
}

export function startEvalRun(
  body: { bank_id: string; mode: EvalMode; graph?: string },
  accessToken: string,
): Promise<ApiResult<{ run_id: string }>> {
  return apiFetch("/api/v1/eval/runs", accessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function cancelEvalRun(
  id: string,
  accessToken: string,
): Promise<ApiResult<{ status: string }>> {
  return apiFetch(
    `/api/v1/eval/runs/${encodeURIComponent(id)}/cancel`,
    accessToken,
    {
      method: "POST",
    },
  );
}

export function listEvalRuns(
  bankId: string,
  accessToken: string,
): Promise<ApiResult<{ runs: RunListRow[] }>> {
  return apiFetch(
    `/api/v1/eval/runs?bank_id=${encodeURIComponent(bankId)}`,
    accessToken,
  );
}

export function getEvalRun(
  id: string,
  accessToken: string,
): Promise<ApiResult<RunDetail>> {
  return apiFetch(`/api/v1/eval/runs/${encodeURIComponent(id)}`, accessToken);
}

/** Trainer P2: promote a chat exchange (or any question) into a bank without
 *  replacing the whole set. The caller's corrected answer is the expected
 *  answer. */
export function appendEvalQuestion(
  bankId: string,
  question: {
    type: QuestionType;
    text: string;
    expected_answer: string;
    unanswerable?: boolean;
  },
  accessToken: string,
): Promise<ApiResult<{ id: string }>> {
  return apiFetch(
    `/api/v1/eval/banks/${encodeURIComponent(bankId)}/questions`,
    accessToken,
    {
      method: "POST",
      body: JSON.stringify(question),
    },
  );
}

/** Trainer P2: download a bank's corpus. `jsonl` = fine-tune-ready NDJSON
 *  (chat-format pairs: verified-pass answer, else expected answer); `json` =
 *  the portable corpus object. */
export async function downloadBankCorpus(
  bankId: string,
  bankName: string,
  format: "json" | "jsonl",
  accessToken: string,
): Promise<{ ok: true } | ApiFailure> {
  const url = `/api/v1/eval/banks/${encodeURIComponent(bankId)}/corpus${
    format === "jsonl" ? "?format=jsonl" : ""
  }`;
  const sessionId = getSessionId();
  const res = await fetchWithRetry503(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
    },
  });
  if (!res.ok) {
    return failureFromResponse(res);
  }
  const blob = await res.blob();
  const safe =
    bankName.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "bank";
  const stem = format === "jsonl" ? "training-set" : "corpus";
  triggerDownload(blob, `${safe}-${stem}.${format}`);
  return { ok: true };
}

/** Trainer: the tenant's training set — every answer a human graded pass,
 *  quick questions and bank runs alike, as fine-tune-ready chat pairs. */
export async function downloadTrainingSet(
  accessToken: string,
): Promise<{ ok: true } | ApiFailure> {
  const sessionId = getSessionId();
  const res = await fetchWithRetry503("/api/v1/eval/training-set", {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
    },
  });
  if (!res.ok) {
    return failureFromResponse(res);
  }
  triggerDownload(await res.blob(), "training-set.jsonl");
  return { ok: true };
}

/** Trainer P3: download a training dataset generated from a graph's declared
 *  ontology. `jsonl` = classification fine-tune corpus (chat format);
 *  `modelfile` = Ollama Modelfile; `qql` = NL→QQL question/query pairs. */
export async function downloadTrainingDataset(
  graphBare: string,
  format: "jsonl" | "modelfile" | "qql",
  accessToken: string,
): Promise<{ ok: true } | ApiFailure> {
  const url = `/api/v1/training/dataset?graph=${encodeURIComponent(graphBare)}&format=${format}`;
  const sessionId = getSessionId();
  const res = await fetchWithRetry503(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
    },
  });
  if (!res.ok) {
    return failureFromResponse(res);
  }
  const blob = await res.blob();
  const ext =
    format === "modelfile"
      ? "Modelfile"
      : format === "qql"
        ? "qql.jsonl"
        : "jsonl";
  triggerDownload(blob, `training-${exportBasename(graphBare)}.${ext}`);
  return { ok: true };
}

/** Trainer P4: curator corrections — guidance the grounding injects as a
 *  bounded prompt block (context only, never data). `alias` maps a user
 *  phrase to a canonical term; `clarification` is a short instruction. */
export interface Correction {
  id: string;
  graph: string;
  kind: "alias" | "clarification";
  phrase: string;
  target: string;
  /** An alias target's label, where the database holds one. */
  target_label?: string;
  note: string;
  created_by: string | null;
  created_at: string;
}

export function listCorrections(
  accessToken: string,
  graphBare?: string,
): Promise<ApiResult<{ corrections: Correction[] }>> {
  const q = graphBare ? `?graph=${encodeURIComponent(graphBare)}` : "";
  return apiFetch(`/api/v1/corrections${q}`, accessToken);
}

export function createCorrection(
  body: {
    graph?: string;
    kind: "alias" | "clarification";
    phrase?: string;
    target?: string;
    note?: string;
  },
  accessToken: string,
): Promise<ApiResult<{ id: string }>> {
  return apiFetch("/api/v1/corrections", accessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteCorrection(
  id: string,
  accessToken: string,
): Promise<ApiResult<null>> {
  return apiFetch(
    `/api/v1/corrections/${encodeURIComponent(id)}`,
    accessToken,
    {
      method: "DELETE",
    },
  );
}

/** Trainer P1: telemetry → "what to do" for one run (manual Ch11's four
 *  levers). Computed server-side from stop reasons, traces, grades, and
 *  sibling runs of the same bank+model+mode. */
export interface EvalRecommendation {
  lever: "config" | "model" | "methodology" | "data";
  severity: "info" | "warn";
  title: string;
  detail: string;
  evidence: string[];
}

export interface EvalQuestionRef {
  result_id: string;
  ordering: number;
  question_text: string;
}

export interface EvalTermGroup {
  term: string;
  questions: EvalQuestionRef[];
  readiness: string | null;
}

export interface EvalPrecheck {
  result_id: string;
  verdict: "likely_pass" | "look_here";
}

export interface EvalWorkList {
  retrieval_empty: EvalTermGroup[];
  retrieval_empty_unnamed: EvalQuestionRef[];
  wrong_despite_hit: EvalQuestionRef[];
  never_asked: EvalQuestionRef[];
  precheck: EvalPrecheck[];
}

export interface EvalRunDelta {
  prior_run_id: string;
  prior_started_at: string;
  fixed: EvalQuestionRef[];
  broke: EvalQuestionRef[];
  still_failing: EvalQuestionRef[];
  not_comparable: number;
}

export interface RunRecommendationsResponse {
  run_id: string;
  recommendations: EvalRecommendation[];
  work_list: EvalWorkList;
  delta: EvalRunDelta | null;
}

export function getRunRecommendations(
  id: string,
  accessToken: string,
): Promise<ApiResult<RunRecommendationsResponse>> {
  return apiFetch(
    `/api/v1/eval/runs/${encodeURIComponent(id)}/recommendations`,
    accessToken,
  );
}

export function gradeEvalResult(
  runId: string,
  resultId: string,
  body: {
    human_grade: HumanGrade | null;
    human_grade_notes?: string;
    criteria_scores?: unknown;
  },
  accessToken: string,
): Promise<ApiResult<null>> {
  return apiFetch(
    `/api/v1/eval/runs/${encodeURIComponent(runId)}/results/${encodeURIComponent(resultId)}`,
    accessToken,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
  );
}

// --- Quick questions (persisted ad-hoc AI exchanges) ---

export interface QuickQuestionRow {
  id: string;
  /** The database that answered; "main" on rows recorded before it was kept. */
  db_id: string;
  asked_by: string;
  asked_by_name: string;
  asked_at: string;
  graph: string | null;
  question_text: string;
  answer_text: string;
  stop_reason: string | null;
  latency_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  model_provider: string | null;
  model_name: string | null;
  human_grade: HumanGrade | null;
  human_grade_notes: string | null;
  graded_by: string | null;
  graded_at: string | null;
  /** Model calls the turn made. Null on rows recorded before list 79.3,
   *  as are the three fields below. */
  llm_calls: number | null;
  cache_read_tokens: number | null;
  cache_write_tokens: number | null;
  /** The per-trip ledger and the limits that applied, JSON-encoded; read by
   *  `TokenomicsPanel`. */
  tokenomics_json: string | null;
}

/** One asked question with the trace the list leaves out, for export. */
export type QuickQuestionTrace = QuickQuestionRow & { trace: AiChatTraceStep[] };

export function fetchQuickQuestionTrace(
  id: string,
  accessToken: string,
): Promise<ApiResult<QuickQuestionTrace>> {
  return apiFetch(`/api/v1/ai/quick-questions/${encodeURIComponent(id)}/trace`, accessToken);
}

/** What changed on the site between two moments that could change an answer:
 *  loads, restores, rule changes, corrections, schema and vocabulary edits. */
export function fetchSiteChanges(
  from: string,
  to: string,
  accessToken: string,
): Promise<ApiResult<{ ts: string; action: string; target: string | null }[]>> {
  return apiFetch(
    `/api/v1/ai/site-changes?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    accessToken,
  );
}

/** Recent quick questions, newest first — the caller's own, or every user's
 *  when the role holds ReviewAllQuestions. */
export function listQuickQuestions(
  accessToken: string,
  limit = 50,
): Promise<ApiResult<QuickQuestionRow[]>> {
  return apiFetch(`/api/v1/ai/quick-questions?limit=${limit}`, accessToken);
}

export function gradeQuickQuestion(
  id: string,
  body: { human_grade: HumanGrade | null; human_grade_notes?: string },
  accessToken: string,
): Promise<ApiResult<null>> {
  return apiFetch(
    `/api/v1/ai/quick-questions/${encodeURIComponent(id)}`,
    accessToken,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
  );
}

// --- SPARQL UPDATE ---

export interface SparqlUpdateRequest {
  update: string;
  /**
   * Set to true to bypass the dangerous-query confirmation gate.
   * The backend returns `requires_confirmation: true` on first
   * submit if the validator flagged the update as risky; the SPA
   * shows a confirmation prompt and re-submits with this flag set.
   */
  confirmed?: boolean;
}

export interface UpdateResponse {
  query_type: string;
  duration_ms: number;
  vdb_rebuild_scheduled: boolean;
}

export interface UpdateErrorBody {
  error?: string;
  detail?: string;
  /** Only present when the validator flagged the update as risky. */
  requires_confirmation?: boolean;
}

/**
 * Run a SPARQL UPDATE. Distinct from `runQuery` — different endpoint,
 * different RBAC gate (`Action::ImportData`), different result shape.
 *
 * On `requires_confirmation: true` the caller should surface a
 * confirmation modal and re-submit with `confirmed: true`.
 */
export async function runUpdate(
  body: SparqlUpdateRequest,
  accessToken: string,
): Promise<ApiResult<UpdateResponse> & { requiresConfirmation?: boolean }> {
  const sessionId = getSessionId();
  const res = await fetchWithRetry503("/api/v1/update", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let errBody: UpdateErrorBody = {};
    try {
      errBody = (await res.json()) as UpdateErrorBody;
    } catch {
      // Non-JSON error — leave empty.
    }
    const out: ApiFailure & { requiresConfirmation?: boolean } = apiFailure(
      res.status,
      errBody.error,
      errBody.detail,
    );
    if (errBody.requires_confirmation) {
      out.requiresConfirmation = true;
    }
    return out;
  }

  const data = (await res.json()) as UpdateResponse;
  return { ok: true, data };
}

// --- Admin: bulk RDF load ---

/** A rule DELETED because a named `@rules "set" { … }` block in the loaded file
 *  did not list it. Named, never merely logged. */
export interface RetractedRule {
  set: string;
  name: string;
}

/** A rule the loaded file carried that did not reach the store. */
export interface RuleLoadFailure {
  name: string;
  error: string;
}

/** What a load did to the rule store — the auditable half of the rule
 *  round-trip. Empty on RDF, which has no rules. */
export interface RuleLoadReport {
  loaded: string[];
  retracted: RetractedRule[];
  failed: RuleLoadFailure[];
  derived: number;
  materialization_error: string | null;
}

export interface LoadResponse {
  bytes: number;
  graph: string;
  duration_ms: number;
  /** What the load has to say beyond its count: its parts, and what it left
   *  out. The name is the field's history, not its content. */
  fuseki_response: string | null;
  rules: RuleLoadReport;
  /** Windows the server cut the upload into; 1 for a small file. */
  parts?: number;
  /** Statements the engine actually WROTE (absent where the backend cannot
   *  count — RDF Graph Store Protocol). The reconcilable truth. */
  statements?: number;
  /** Held values a later value replaced, on a property the ontology holds to
   *  one value; absent when none was. */
  updated?: number;
  /** Values not written because the store holds a later one; absent when
   *  none was. */
  not_applied_older?: number;
}

/** What loading a file WOULD do to one named ruleset — computed server-side
 *  without writing. `would_retract` names the rules that vanish if it commits. */
export interface RulesetPreflight {
  set: string;
  incoming: number;
  would_retract: string[];
}

export interface RulePreflight {
  sets: RulesetPreflight[];
  unnamed: number;
  total_would_retract: number;
}

export type LoadKind = "ontology" | "vocabulary" | "data" | "catalog";

/**
 * Write semantics on the load endpoint.
 * - `append` (default) — add to existing data; no destruction.
 * - `replace` — drop the target graph entirely first, then write.
 */
export type LoadMode = "append" | "replace";

/** Create an (empty) custom named graph, optionally declaring a role
 *  (ontology | vocabulary | data | catalog) so role-aware surfaces treat
 *  it like its managed counterpart. */
export async function createGraph(
  uri: string,
  idToken: string,
  role?: string,
): Promise<ApiResult<unknown>> {
  return apiFetch<unknown>("/api/v1/admin/graphs", idToken, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uri, ...(role ? { role } : {}) }),
  });
}

/** Drop a custom named graph (delete all its statements). Managed graphs are protected. */
export async function deleteGraph(
  uri: string,
  idToken: string,
): Promise<ApiResult<unknown>> {
  return apiFetch<unknown>(
    `/api/v1/admin/graphs?uri=${encodeURIComponent(uri)}`,
    idToken,
    {
      method: "DELETE",
    },
  );
}

// --- Source provenance (@source attestations; QROS builds only) ---

export interface SourceCount {
  source: string;
  statements: number;
  /** Statements this source alone attests — what deleting it would remove. */
  sole_attested: number;
  /** The LAST load of this source, not every load: Delete acts on the source as
   *  a whole, so per-load rows would offer an action the store cannot honour.
   *  Null for statements loaded before the loader stamped these. */
  last_imported_at: string | null;
  last_loaded_by: string | null;
}

export interface SourceDeleteReport {
  statements_deleted: number;
  attestations_stripped: number;
}

export function listSources(
  idToken: string,
): Promise<ApiResult<{ sources: SourceCount[] }>> {
  return apiFetch("/api/v1/admin/sources", idToken);
}

/** A source delete is a JOB (45.7.3): a source can span tens of millions of
 *  statements, and a held-open request taught the browser to report failure
 *  while the server deleted for an hour. DELETE answers 202 at once; poll. */
export interface SourceDeleteAccepted {
  job_id: string;
  status_url: string;
}

export interface SourceDeleteJobStatus {
  job_id: string;
  state: "running" | "done" | "failed";
  source: string;
  started_at: string;
  elapsed_ms: number;
  progress: { scanned: number; deleted: number; stripped: number };
  result?: SourceDeleteReport;
  http_status?: number;
  error?: string;
  detail?: string;
}

export function deleteSource(
  source: string,
  idToken: string,
  /** Narrow to one load of the source: the moment it was loaded. */
  importedAt?: string,
): Promise<ApiResult<SourceDeleteAccepted>> {
  return apiFetch(
    `/api/v1/admin/sources?source=${encodeURIComponent(source)}${
      importedAt ? `&imported_at=${encodeURIComponent(importedAt)}` : ""
    }`,
    idToken,
    {
      method: "DELETE",
    },
  );
}

export function sourceDeleteJobStatus(
  jobId: string,
  idToken: string,
): Promise<ApiResult<SourceDeleteJobStatus>> {
  return apiFetch(
    `/api/v1/admin/sources/jobs/${encodeURIComponent(jobId)}`,
    idToken,
  );
}

export interface GraphClearReport {
  graph: string;
  /** Null when the backend could not count what it removed. */
  statements_removed: number | null;
}

/** A clear is a JOB (UAT 2026-09-02): the data graph can span millions of
 *  statements, and a held-open request timed out in the browser while the
 *  server kept deleting — the same lesson as the source delete. Both clear
 *  endpoints answer 202 at once; poll the status URL. */
export interface GraphClearAccepted {
  job_id: string;
  status_url: string;
}

/** Where a running clear stands (#113): queued, and what it waits behind, or
 *  clearing. */
export interface GraphClearProgress {
  /** `finished` when the job is read back after a restart. */
  stage: "queued" | "clearing" | "finished";
  behind?: string;
  /** The site restarted while this clear waited or ran, and took it up again. */
  restarted?: boolean;
  /** Prefixes the clear took off the Namespaces list: recorded from data, and
   *  used by no statement left on the site. */
  prefixes_removed?: string[];
  /** Rules the derivation pass after the clear skipped because they do not
   *  compile, each with its reason; the clear itself is done. */
  rules_skipped?: { name: string; problem: string }[];
}

export interface GraphClearJobStatus {
  job_id: string;
  state: "running" | "done" | "failed";
  progress: GraphClearProgress;
  graphs: string[];
  started_at: string;
  elapsed_ms: number;
  result?: GraphClearReport[];
  http_status?: number;
  error?: string;
  detail?: string;
}

/** Empty a named graph — managed or custom — keeping the graph itself
 *  (discovery marker and declared role survive). Accepted as a job; poll. */
export async function clearGraph(
  uri: string,
  idToken: string,
): Promise<ApiResult<GraphClearAccepted>> {
  return apiFetch<GraphClearAccepted>("/api/v1/admin/graphs/clear", idToken, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uri }),
  });
}

/** Empty several graphs as ONE write and ONE index rebuild — the operator's
 *  "clear and re-stamp". Four sequential clears rebuilt and re-embedded data
 *  the next clear deleted and OOM-killed a 60 GB task (2026-08-27). Accepted
 *  as a job; poll. */
export async function clearGraphs(
  uris: string[],
  idToken: string,
): Promise<ApiResult<GraphClearAccepted>> {
  return apiFetch<GraphClearAccepted>(
    "/api/v1/admin/graphs/clear-all",
    idToken,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ uris }),
    },
  );
}

export function graphClearJobStatus(
  jobId: string,
  idToken: string,
): Promise<ApiResult<GraphClearJobStatus>> {
  return apiFetch(
    `/api/v1/admin/graphs/clear/jobs/${encodeURIComponent(jobId)}`,
    idToken,
  );
}

/** Managed graph bare-IRIs → their LoadKind shortcut; custom graphs map to none. */
const MANAGED_LOAD_KINDS: Record<string, LoadKind> = {
  "urn:tres:ontology": "ontology",
  "urn:tres:vocabulary": "vocabulary",
  "urn:tres:data": "data",
  "urn:tres:catalog": "catalog",
};

/** The load target query fragment: `kind=…` for a managed graph, else
 *  `graph=<uri>` for a custom named graph. */
function loadTargetParam(graphBare: string): string {
  const kind = MANAGED_LOAD_KINDS[graphBare];
  return kind ? `kind=${kind}` : `graph=${encodeURIComponent(graphBare)}`;
}

/** A load job as the server reports it (`GET /api/v1/admin/load/jobs/{id}`). */
/** How far a load job has come: windows loaded, bytes of the upload the
 *  server has consumed, statements written so far. */
export interface LoadJobProgress {
  part: number;
  bytes_done: number;
  bytes_total: number;
  statements: number;
}

/** The load gate: whether an import holds it right now, and what. The SPA polls
 *  this to show the upload ban on every upload surface while a load runs — one
 *  load at a time, so a concurrent upload can't race it (the VDB-collision ban). */
export interface LoadInProgress {
  label: string;
  /** `queued-clear`: nothing runs, but clears wait for the gate, so loads wait too (#113). */
  kind: "file" | "folder" | "clear" | "queued-clear";
  started_at: string;
}

export function loadGateStatus(
  idToken: string,
): Promise<ApiResult<{ in_progress: LoadInProgress | null }>> {
  return apiFetch("/api/v1/admin/load/status", idToken);
}

export interface LoadJobStatus {
  job_id: string;
  state: "running" | "done" | "failed";
  graph: string;
  /** The database the load writes to. */
  database?: string;
  filename?: string;
  bytes: number;
  started_at: string;
  elapsed_ms: number;
  progress: LoadJobProgress;
  result?: LoadResponse;
  http_status?: number;
  error?: string;
  detail?: string;
}

/**
 * Start a background load JOB. The server accepts the whole file at once and
 * returns a `job_id`; the load then runs server-side, so this resolves as soon
 * as the upload has arrived rather than blocking for the minutes a large load
 * takes. The `LoadJobsProvider` polls `loadJobStatus` to learn the outcome and
 * raise the completion notice. Engineered not to throw — a fetch-level failure
 * comes back as an `ApiResult` error.
 */
export async function startLoadJob(
  body: Blob,
  contentType: string,
  graphBare: string,
  idToken: string,
  mode: LoadMode = "append",
  filename?: string,
): Promise<ApiResult<{ job_id: string }>> {
  const filePart = filename ? `&filename=${encodeURIComponent(filename)}` : "";
  const url = `/api/v1/admin/load/jobs?${loadTargetParam(graphBare)}&mode=${mode}${filePart}`;
  const sessionId = getSessionId();

  let res: Response;
  try {
    res = await fetchWithRetry503(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": contentType,
        ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
      },
      body,
    });
  } catch (e) {
    // Connection failure, abort, or any other fetch-level reject.
    return apiFailure(0, "network error", e instanceof Error ? e.message : String(e));
  }

  if (!res.ok) {
    let errBody: { error?: string; detail?: string } = {};
    try {
      errBody = (await res.json()) as { error?: string; detail?: string };
    } catch {
      // Non-JSON error body (the ALB, the rate limiter, a proxy). Try once
      // more as text so we can still surface *something*.
      try {
        const text = await res.clone().text();
        if (text.trim().length > 0) {
          errBody = { detail: text };
        }
      } catch {
        // Body already consumed or unreadable — fall through.
      }
    }
    return apiFailure(res.status, errBody.error, errBody.detail ?? undefined);
  }

  let accepted: { job_id?: string };
  try {
    accepted = (await res.json()) as { job_id?: string };
  } catch (e) {
    return apiFailure(502, "The server's answer could not be read", e instanceof Error ? e.message : String(e));
  }
  if (!accepted.job_id) {
    return apiFailure(502, "The server accepted the load but gave no way to follow it. Look for the load under Recent loads");
  }
  return { ok: true, data: { job_id: accepted.job_id } };
}

/** This user's recent load jobs (newest first). The `LoadJobsProvider`
 *  reconciles the jobs it is watching against this on mount / tab-focus, so a
 *  load that finished while the id-token was expired mid-poll, or while the tab
 *  was closed, is still reported once the session is valid again. The result
 *  lives in the server's job store for a day — never in a live polling token. */
export function listLoadJobs(
  idToken: string,
): Promise<ApiResult<{ jobs: LoadJobStatus[] }>> {
  return apiFetch<{ jobs: LoadJobStatus[] }>(
    "/api/v1/admin/load/jobs",
    idToken,
  );
}

// --- Admin: load from configured git source ---

export interface GitSource {
  id: string;
  name: string;
  base_url: string;
  /** Whether the server has a PAT configured for this source. SPA
   *  uses it only as a visual hint ("public" vs "authenticated") —
   *  the PAT itself never leaves the server. */
  authenticated: boolean;
}

interface ListGitSourcesResponse {
  sources: GitSource[];
}

/** Returns the list of remote RDF sources the operator has configured
 *  (via `TRES_API_GIT_SOURCES`). Empty array when none are configured;
 *  the SPA hides the "Load from Git" tab in that case. */
export async function listGitSources(
  accessToken: string,
): Promise<ApiResult<ListGitSourcesResponse>> {
  return apiFetch<ListGitSourcesResponse>(
    "/api/v1/admin/git-sources",
    accessToken,
  );
}

export interface LoadFromGitRequest {
  source_id: string;
  path: string;
  /** Target graph URI — same contract as the direct upload. */
  graph: string;
  mode?: LoadMode;
}

/** Fetch a file from a configured remote source (server-side) and
 *  load it into the chosen managed graph. Server attaches the source's
 *  bearer PAT when configured; we never send credentials from the SPA. */
export async function loadFromGit(
  body: LoadFromGitRequest,
  accessToken: string,
): Promise<ApiResult<LoadResponse>> {
  return apiFetch<LoadResponse>("/api/v1/admin/load-from-git", accessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- Namespace registry ---

export interface NamespacePrefix {
  prefix: string;
  uri: string;
  /** Compiled-in default (true) vs user-added (false). The SPA hides
   *  the delete button on builtins; the server still allows
   *  overriding a builtin by upserting a same-prefix entry. */
  builtin: boolean;
  /** Recorded by the store from a loaded file; queries resolve against it. */
  imported: boolean;
  /** Statements that use this binding in the kept prefix usage check; null
   *  when no kept check covered it. */
  usage: number | null;
  /** Why the prefix or its namespace fails the add-prefix checks. */
  invalid: string | null;
  /** Whether Remove unused would take this binding now. */
  removable: boolean;
}

export interface NamespaceList {
  prefixes: NamespacePrefix[];
  /** When the kept prefix usage check finished; null before any. */
  usage_checked_at: string | null;
  /** Whether the kept check read the store as it is now. */
  usage_current: boolean;
}

/** Mirror of the server's prefix usage check status. */
export interface NamespaceUsageRun {
  started_at: string;
  started_by: string;
}

export interface NamespaceUsageStatus {
  running: NamespaceUsageRun | null;
  last:
    | (NamespaceUsageRun & {
        finished_at: string;
        duration_ms: number;
        statements: number;
        counts: { prefix: string; uri: string; statements: number }[];
      })
    | null;
  last_failure:
    | (NamespaceUsageRun & {
        finished_at: string | null;
        duration_ms: number | null;
        error: string;
        detail?: string | null;
      })
    | null;
  /** The other whole-store analysis the running check waits behind. */
  waiting_for: AnalysisRun | null;
  /** Whether `last` read the store as it is now. */
  current: boolean;
}

/** Where the prefix usage check stands. Open to any signed-in user. */
export async function namespaceUsageStatus(
  accessToken: string,
): Promise<ApiResult<NamespaceUsageStatus>> {
  return apiFetch<NamespaceUsageStatus>(
    "/api/v1/namespaces/usage",
    accessToken,
  );
}

/** Start the prefix usage check in the background. PowerUser+. */
export async function runNamespaceUsageCheck(
  accessToken: string,
): Promise<ApiResult<NamespaceUsageStatus>> {
  return apiFetch<NamespaceUsageStatus>(
    "/api/v1/namespaces/usage/run",
    accessToken,
    {
      method: "POST",
    },
  );
}

/** Fetch the merged list of namespace prefixes (compiled-in defaults
 *  overlaid with user additions persisted in the SQLite state layer).
 *  Open to any signed-in user. */
export async function listNamespaces(
  accessToken: string,
): Promise<ApiResult<NamespaceList>> {
  return apiFetch<NamespaceList>("/api/v1/namespaces", accessToken);
}

export interface RemoveUnusedResult {
  removed: string[];
  /** Named prefixes the server kept, with why in plain words. */
  kept: { prefix: string; reason: string }[];
}

/** Remove the named bindings recorded from data that the kept check found no
 *  statement using. PowerUser+. Refused while a load runs or when the data
 *  may have changed since the check; built-in and custom bindings stay. */
export async function removeUnusedNamespaces(
  prefixes: string[],
  accessToken: string,
): Promise<ApiResult<RemoveUnusedResult>> {
  return apiFetch<RemoveUnusedResult>(
    "/api/v1/namespaces/remove-unused",
    accessToken,
    {
      method: "POST",
      body: JSON.stringify({ prefixes }),
    },
  );
}

/** Add or update a prefix → URI binding. PowerUser+ only — server
 *  enforces the gate via Action::ManageDashboards. */
export async function upsertNamespace(
  prefix: string,
  uri: string,
  accessToken: string,
): Promise<ApiResult<NamespacePrefix>> {
  return apiFetch<NamespacePrefix>("/api/v1/namespaces", accessToken, {
    method: "POST",
    body: JSON.stringify({ prefix, uri }),
  });
}

/** Remove a user-added prefix. PowerUser+ only. The server returns
 *  204 No Content on success regardless of whether the prefix existed. */
export async function deleteNamespace(
  prefix: string,
  accessToken: string,
): Promise<ApiResult<null>> {
  return apiFetch<null>(
    `/api/v1/namespaces?prefix=${encodeURIComponent(prefix)}`,
    accessToken,
    {
      method: "DELETE",
    },
  );
}

// --- Graph export ---

// `tqd`/`tqs` are QROS-native (data / schema); the RDF line formats are
// RDF-backend only.
export type ExportFormat =
  "turtle" | "ntriples" | "nquads" | "rdfxml" | "jsonld" | "tqd" | "tqs";

const EXPORT_EXTENSION: Record<ExportFormat, string> = {
  turtle: "ttl",
  ntriples: "nt",
  nquads: "nq",
  rdfxml: "rdf",
  jsonld: "jsonld",
  tqd: "tqd",
  tqs: "tqs",
};

/**
 * Fetch a managed graph from /api/v1/admin/export and trigger a
 * browser download. The endpoint streams whatever serialization the
 * server returns; we attach a stable filename
 * (`tres-<kind>.<ext>`) and let the browser save dialog handle the
 * rest. Returns a discriminated result so the caller can surface
 * errors inline.
 */
export async function downloadGraph(
  graphBare: string,
  format: ExportFormat,
  accessToken: string,
): Promise<
  { ok: true } | ApiFailure
> {
  const url = `/api/v1/admin/export?${loadTargetParam(graphBare)}&format=${format}`;
  const sessionId = getSessionId();
  const res = await fetchWithRetry503(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
    },
  });

  if (!res.ok) return failureFromResponse(res);

  const blob = await res.blob();
  const basename = MANAGED_LOAD_KINDS[graphBare] ?? exportBasename(graphBare);
  // The server names the file for what it holds: a schema graph picked as .tqd
  // comes back as .tqs, because that is the only form the loader takes back.
  const filename =
    filenameFromDisposition(res.headers.get("content-disposition")) ??
    `tres-${basename}.${EXPORT_EXTENSION[format]}`;
  triggerDownload(blob, filename);
  return { ok: true };
}

/** The file name a Content-Disposition header carries, or null when it carries none. */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const quoted = /filename="([^"]+)"/.exec(header);
  if (quoted?.[1]) return quoted[1];
  const bare = /filename=([^;\s]+)/.exec(header);
  return bare?.[1] ?? null;
}

/** Download the Vocabulary/Ontology documentation export (HTML or Markdown)
 *  for a graph — the desktop documentation feature, served by the API — or a
 *  vocabulary as a spreadsheet (CSV) the spreadsheet import reads back. A
 *  spreadsheet the server had to cap carries a note saying what it left out. */
export async function downloadDocExport(
  kind: "vocab" | "ontology",
  graphBare: string,
  format: "html" | "markdown" | "csv",
  accessToken: string | undefined,
): Promise<
  | { ok: true; note: string | null }
  | ApiFailure
> {
  const url = `/api/v1/${kind}/export-doc?graph=${encodeURIComponent(graphBare)}&format=${format}`;
  const res = await fetchWithRetry503(url, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) return failureFromResponse(res);
  const blob = await res.blob();
  const name = kind === "vocab" ? "vocabulary" : "ontology";
  triggerDownload(
    blob,
    format === "csv"
      ? `${name}.csv`
      : `${name}-documentation.${format === "html" ? "html" : "md"}`,
  );
  return { ok: true, note: res.headers.get("X-Tres-Export-Note") };
}

/** Download the rule store as native `.tqs`. Without this a backup captures
 *  statements and loses the entire rule layer. `set` narrows to one named
 *  ruleset; omitted exports the whole store, app-authored rules included. */
export async function downloadRules(
  set: string | null,
  accessToken: string,
): Promise<
  { ok: true } | ApiFailure
> {
  const url = set
    ? `/api/v1/rules/export?set=${encodeURIComponent(set)}`
    : "/api/v1/rules/export";
  const sessionId = getSessionId();
  const res = await fetchWithRetry503(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
    },
  });
  if (!res.ok) return failureFromResponse(res);
  const blob = await res.blob();
  triggerDownload(
    blob,
    set ? `tres-rules-${exportBasename(set)}.tqs` : "tres-rules.tqs",
  );
  return { ok: true };
}

/** Ask the server what loading `content` would RETRACT from the rule store,
 *  writing nothing. The gate in front of the destructive named-set replace. */
export function preflightRules(
  content: string,
  accessToken: string,
): Promise<ApiResult<RulePreflight>> {
  return apiFetch<RulePreflight>("/api/v1/rules/preflight", accessToken, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: content,
  });
}

/** Load ONLY the `@rules` blocks of a native `.tqs` — the Rules-page path.
 *  The server refuses statement-bearing files: statements need a graph target
 *  and belong to the Database Management load, while rules need neither a
 *  graph nor a mode, which is the point of this endpoint. */
export function loadRulesFile(
  content: string,
  accessToken: string,
): Promise<ApiResult<RuleLoadReport>> {
  return apiFetch<RuleLoadReport>("/api/v1/rules/load", accessToken, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: content,
  });
}

/** Filesystem-safe basename from a custom graph URI's local name. Mirrors the
 *  server's `export_basename` so the suggested filename matches either way. */
function exportBasename(graphBare: string): string {
  const local = graphBare.split(/[/#:]/).filter(Boolean).pop() ?? graphBare;
  const cleaned = local.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return cleaned.length > 0 ? cleaned.toLowerCase() : "graph";
}

/**
 * Create a transient blob URL, click a hidden anchor with the
 * `download` attribute, and revoke the URL. Universal browser
 * pattern for "save this client-generated content as a file."
 * Used by both downloadGraph (server bytes) and the Query page's
 * client-side CSV/TSV conversion.
 */
export function triggerDownload(
  content: Blob | string,
  filename: string,
  mimeType?: string,
) {
  const blob =
    typeof content === "string"
      ? new Blob([content], { type: mimeType ?? "text/plain;charset=utf-8" })
      : content;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/// Map common RDF file extensions to the MIME type Fuseki expects on
/// the Graph Store Protocol endpoint. Fall through to `text/turtle`
/// since `.ttl` is the most common and Fuseki happily auto-detects on
/// many serializations.
export function rdfContentType(filename: string): string {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".ttl")) return "text/turtle";
  if (lower.endsWith(".nt")) return "application/n-triples";
  if (lower.endsWith(".nq")) return "application/n-quads";
  if (lower.endsWith(".trig")) return "application/trig";
  if (lower.endsWith(".rdf") || lower.endsWith(".xml"))
    return "application/rdf+xml";
  if (lower.endsWith(".jsonld")) return "application/ld+json";
  // Plain .json is QROS JSON (statement objects or [s,p,o] arrays), not
  // JSON-LD — the substrate sniffs which flavor.
  if (lower.endsWith(".json")) return "application/json";
  // QROS-native uploads — the substrate parses these by content type (P2f).
  if (lower.endsWith(".tqs")) return "application/x-qros-schema";
  if (lower.endsWith(".tqd")) return "application/x-qros-data";
  return "text/turtle";
}

// --- Spreadsheet import (AI-first, QROS-native) ---
//
// Mirrors the serde shapes of `tres_qros_core::convert::spreadsheet` and the
// `handlers::import` DTOs. The flow is stateless: `profileSpreadsheet` returns
// the materialized rows, which the caller echoes back on preview/commit.

export type InferredType =
  "empty" | "integer" | "decimal" | "boolean" | "date" | "iri" | "text";

export type SheetRow = Record<string, string>;

export interface ColumnProfile {
  name: string;
  index: number;
  non_blank: number;
  blank: number;
  distinct: number;
  inferred: InferredType;
  samples: string[];
  /** Non-blank cells that carried an RDF datatype marker (`^^type`), folded
   *  to the bare value on import. Language markers are kept. 0 = clean. */
  rdf_literal_cells?: number;
  /** Fraction of non-blank cells matching the dominant type (1.0 = clean). */
  conformance: number;
  /** A few cells that do not match the dominant type. */
  nonconforming_samples: string[];
}

/** A near-functional dependency determinant → dependent (latent-entity signal). */
export interface Dependency {
  determinant: string;
  dependent: string;
  confidence: number;
}

export interface SheetProfile {
  row_count: number;
  columns: ColumnProfile[];
  dependencies: Dependency[];
}

export interface SpreadsheetProfileResponse {
  columns: string[];
  rows: SheetRow[];
  profile: SheetProfile;
  sheet_names: string[];
  selected_sheet: string | null;
  /** First few RAW rows (no header interpretation) for the header row-picker. */
  raw_rows: string[][];
  /** The header row this response was profiled with (zero-indexed). */
  header_row: number;
  /** The first data row this response was profiled with (zero-indexed). */
  data_start_row: number;
  /** Saved mappings that fit these headers, best match first, each with its
   *  spec already fitted to this file's columns. */
  templates: TemplateMatch[];
  /** What the reader did to make a table of the file (values joined, nested
   *  objects kept as text). Shown before mapping. */
  notes: string[];
}

export type TemplateSourceKind = "spreadsheet" | "json";

/** A saved mapping: an ImportSpec under a site-unique name, with the header
 *  set it was built against. */
export interface ImportTemplate {
  id: string;
  name: string;
  source_kind: TemplateSourceKind;
  columns: string[];
  spec: ImportSpec;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
}

/** How a saved mapping relates to the uploaded headers: `exact` = same
 *  column set; `subset` = every template column present, extras skipped;
 *  `overlap` = at least half present, the rest dropped. */
export interface TemplateMatch {
  id: string;
  name: string;
  source_kind: TemplateSourceKind;
  match: "exact" | "subset" | "overlap";
  missing_columns: string[];
  extra_columns: string[];
  spec: ImportSpec;
  last_used_at: string | null;
}

export async function listImportTemplates(
  idToken: string,
): Promise<ApiResult<{ templates: ImportTemplate[] }>> {
  return apiFetch<{ templates: ImportTemplate[] }>(
    "/api/v1/import/templates",
    idToken,
  );
}

export async function getImportTemplate(
  id: string,
  idToken: string,
): Promise<ApiResult<ImportTemplate>> {
  return apiFetch<ImportTemplate>(
    `/api/v1/import/templates/${encodeURIComponent(id)}`,
    idToken,
  );
}

export async function saveImportTemplate(
  body: {
    name: string;
    source_kind?: TemplateSourceKind;
    columns: string[];
    spec: ImportSpec;
  },
  idToken: string,
): Promise<ApiResult<ImportTemplate>> {
  return apiFetch<ImportTemplate>("/api/v1/import/templates", idToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function deleteImportTemplate(
  id: string,
  idToken: string,
): Promise<ApiResult<null>> {
  return apiFetch<null>(
    `/api/v1/import/templates/${encodeURIComponent(id)}`,
    idToken,
    {
      method: "DELETE",
    },
  );
}

/** A deterministic cell-value transform — serde-tagged on `op`. Mirrors
 *  `tres_qros_core::convert::spreadsheet::Normalize`. */
export type Normalize =
  | { op: "trim" }
  | { op: "collapse" }
  | { op: "lowercase" }
  | { op: "uppercase" }
  | { op: "replace"; from: string; to: string }
  | { op: "date_iso" }
  /** A time of day into ISO `HH:MM:SS`. */
  | { op: "time_iso" }
  /** A date and time in one cell into ISO `YYYY-MM-DDTHH:MM:SS`. */
  | { op: "date_time_iso" }
  | { op: "zero_pad"; width: number }
  | { op: "percent_to_fraction" }
  | { op: "prepend"; value: string }
  /** Missing-value sentinel: map these tokens to blank so the statement is
   *  dropped. Per-column ("X" = n/a here, but a real code elsewhere). */
  | { op: "blank_if"; tokens: string[] }
  /** Regex replacement (e.g. `\.` → `-`). Invalid pattern is a no-op. */
  | { op: "regex_replace"; pattern: string; replacement: string }
  /** Map y/yes/1/t → true, n/no/0/f → false; else unchanged. */
  | { op: "to_boolean" };

/** How the row's subject IRI is minted from the key column. Serde-tagged on
 *  `kind`. Mirrors `tres_qros_core::convert::import_spec::IdStrategy`. */
export type IdStrategy =
  | { kind: "from_column" }
  /** Readable composite: append these columns to the key when it is not unique
   *  on its own (`{Series}_{Air Time}`). */
  | { kind: "composite"; also: string[] }
  /** Surrogate: a stable hash of every column, for sheets with no natural id. */
  | { kind: "surrogate" }
  /** The column's text names each entity without identifying it: a UUID over
   *  the entity's class and the folded name, labelled by the name (ruling 4.8). */
  | { kind: "generated" };

/** How an entity resolves to an identity — the desktop Instances strategy.
 *  Serde internally-tagged on `mode`. */
export type KeyResolution =
  | { mode: "mint" }
  | {
      mode: "match_by_label";
      create_if_missing: boolean;
      label_column?: string | null;
    }
  | { mode: "match_by_id"; create_if_missing: boolean };

/** One column's ontology designation — the authoring layer the wizard edits,
 *  lowered to a `NativeMapping` server-side. Serde-tagged on `role`. Mirrors
 *  `tres_qros_core::convert::import_spec::ColumnRole`.
 *
 *  A sheet may have any number of `instance` (ID) columns, and no row is one
 *  record (ruling 4.8). Every `from_column` names the ID column a statement
 *  describes; null means the sole `instance` column, and is an error when the
 *  spec has several. */
/** One relationship an ID column's thing holds with the thing another ID
 *  column names on the same row. */
export interface EntityLink {
  predicate: string;
  to_column: string;
  context?: string;
}

export type ColumnRole =
  | { role: "skip" }
  | {
      role: "instance";
      class: string | null;
      is_label: boolean;
      fill_down: boolean;
      resolution: KeyResolution;
      id_strategy: IdStrategy;
      /** Relationships from this column's thing to the thing another ID
       *  column names on the same row, declared where they start. */
      links?: EntityLink[];
    }
  | { role: "label"; from_column?: string | null }
  | { role: "comment"; from_column?: string | null }
  | { role: "record_kind"; from_column?: string | null }
  | {
      role: "object_property";
      predicate: string;
      range_class: string | null;
      resolution: KeyResolution;
      required: boolean;
      /** The text names each referenced thing: a new one gets a UUID over its
       *  class and name instead of a slug of the name. */
      generated_id?: boolean;
      /** The ID column the reference relates FROM (`team :orgWithin org`). */
      from_column?: string | null;
      /** The ID column whose thing this statement holds in (`@context`):
       *  a player's minutes in a game. */
      context?: string | null;
      /** Relationships from the referenced thing onward (UAT 2026-09-27). */
      links?: EntityLink[];
    }
  | {
      role: "datatype_property";
      predicate: string;
      datatype: string | null;
      required: boolean;
      /** The ID column the value is a fact about — a game's venue on a sheet
       *  that also names players. */
      from_column?: string | null;
      /** The ID column whose thing this statement holds in (`@context`):
       *  a player's minutes in a game. */
      context?: string | null;
      /** A date and time in two columns: the column the date is read from. */
      date_column?: string | null;
    }
  | {
      /** The values MEAN SOMETHING OTHER THAN THEMSELVES (ruled 2026-09-25):
       *  `B` stands for a term of sale, `Y` says the line item is Major
       *  Defense Equipment. A rule says what; a value the rule does not cover
       *  asserts nothing. A cell whose value means itself is not a code — a
       *  number is a `datatype_property`, a term's full name is an
       *  `object_property` matched by name. */
      role: "code";
      /** Whose statement it is: the ID column it describes. */
      from_column?: string | null;
      /** The ID column whose thing this statement holds in (`@context`):
       *  a player's minutes in a game. */
      context?: string | null;
      rule: CodeRule;
      /** Relationships from the term a code names onward. */
      links?: EntityLink[];
    }
  | { role: "tag"; tag: string };

/** What a code column's values mean. Mirrors
 *  `tres_qros_core::convert::import_spec::CodeRule`. */
export type CodeRule =
  | {
      /** The value names one term of `class`, found by its code and looked up
       *  in that list alone — so the same letter in another column can mean
       *  something else. */
      reads: "term";
      predicate: string;
      class: string;
      create_if_missing: boolean;
    }
  | {
      /** Each listed value asserts statements outright; the code itself is
       *  recorded nowhere. */
      reads: "means";
      values: CodeMeaning[];
    };

export interface CodeMeaning {
  value: string;
  asserts: CodeAssertion[];
}

export interface CodeAssertion {
  predicate: string;
  object: string;
  /** The object names a thing rather than being a literal. */
  object_is_iri: boolean;
}

export interface ColumnSpec {
  column: string;
  role: ColumnRole;
  normalize: Normalize[];
  /** A blank cell means the same as the one above (report-shaped sheets).
   *  An `instance` column carries down through its own `fill_down`. */
  fill_down?: boolean;
}

/** A code list a column is read in. Mirrors
 *  `tres_qros_core::convert::spreadsheet::CodeList`. */
export interface CodeList {
  class: string;
}

/** The wizard's editable import spec. */
export interface ImportSpec {
  namespace: string;
  columns: ColumnSpec[];
  /** Classes created while mapping, each with the parent the dialog
   *  required. The import declares them once. */
  new_classes?: NewClass[];
  /** The columns that identify a thing and the name each identifier goes by
   *  across sheets (61.54): a thing another sheet knows under one of them is
   *  found before it is created. */
  identifiers?: SpecIdentifier[];
}

export interface SpecIdentifier {
  /** The column that holds the identifier. */
  column: string;
  /** The Entity column whose thing it identifies: itself when its own values
   *  are that thing's identity on this sheet. */
  of_column: string;
  /** The identifier's name, `isbn`; its values are stored on the thing under
   *  the property of that name. */
  name: string;
}

export interface NewClass {
  class: string;
  label: string;
  parent: string;
}

/** One dropped row + why (the data-quality report). */
export interface SkippedRow {
  row: number;
  reason: string;
}

export interface GenerationReport {
  rows_in: number;
  rows_skipped_no_subject: number;
  entities_minted: number;
  entities_reconciled: number;
  relations_out: number;
  /** Distinct relationship statements: what the store holds once rows that
   *  repeat one are stored once. */
  relations_distinct?: number;
  tags_applied: number;
  statements_out: number;
  /** Statements struck by the user (per-statement ignore). */
  statements_ignored: number;
  skipped: SkippedRow[];
  /** Match-only references with no match — left out, counted. */
  references_dropped: number;
  /** Cells that are not a value of their column's declared datatype — no
   *  statement emitted, each one listed. */
  values_rejected: RejectedValue[];
  /** Classes minted from a type column whose value matched no declared
   *  class: the row is typed by the new class, which the user can merge into
   *  an existing one on the Ontology page later. */
  classes_created?: CreatedClass[];
  /** Terms and records a reference column creates because the value matched
   *  nothing already in the store — listed before commit. */
  references_created?: CreatedReference[];
  /** Codes that name more than one term of their code list: not linked,
   *  listed so the list can be corrected. */
  codes_ambiguous?: AmbiguousCode[];
}

export interface AmbiguousCode {
  column: string;
  class: string;
  code: string;
  terms: string[];
  rows: number;
}

export interface CreatedReference {
  column: string;
  class: string | null;
  count: number;
  /** The first few values the new ones are named by. */
  examples: string[];
}

export interface CreatedClass {
  class: string;
  label: string;
  parent: string;
  rows: number;
}

export interface RejectedValue {
  row: number;
  column: string;
  value: string;
  datatype: string;
}

export interface PreviewTag {
  name: string;
  value: string;
  /** The name of the thing the value refers to (a game's, for a context). */
  label?: string;
}

/** One fact the import will store. Rows that state the same fact are one
 *  entry, with how many rows state it. */
export interface PreviewStatement {
  /** Law-1 statement identity (hex) — the key for leaving it out. */
  id: string;
  subject: string;
  /** The subject's name and class: "Adam Smith", ":Player". */
  subject_label?: string;
  subject_class?: string;
  /** How many rows name the subject. */
  subject_rows: number;
  predicate: string;
  object: string;
  object_is_iri: boolean;
  object_label?: string;
  /** 1-based data-row ordinal (header excluded) of the first row stating it. */
  row: number;
  /** How many rows state it. */
  stated_on: number;
  /** Source column the value came from; null for declared constants (:type). */
  column: string | null;
  tags: PreviewTag[];
}

export interface CollapseGroup {
  subject: string;
  predicate: string;
  count: number;
  sample_object: string;
}

export interface FidelityReport {
  rows_in: number;
  generated: number;
  distinct: number;
  collapsed: number;
  groups: CollapseGroup[];
  hint: string | null;
}

/** One of the values a thing holds for a property. */
export interface HeldValue {
  value: string;
  label?: string;
  /** How many rows state it. */
  rows: number;
  /** The statement that carries it, for leaving that one out. */
  statement_id: string;
}

/** A thing the sheet gives several values for one property, by name. */
export interface ValuesHeld {
  subject: string;
  subject_label?: string;
  subject_class?: string;
  predicate: string;
  /** The context the values share, when they share one. */
  context?: string;
  declared_max?: number;
  values: HeldValue[];
}

/** One property of one class of things: how many of those things hold
 *  several values for it, and the first few by name. With `declared_max` the
 *  ontology says the property holds fewer, and it is a conflict; without, it
 *  is information. Neither holds the import. */
export interface PropertyValues {
  predicate: string;
  subject_class?: string;
  declared_max?: number;
  things: number;
  examples: ValuesHeld[];
}

export interface SpreadsheetPreviewResponse {
  statements: PreviewStatement[];
  report: GenerationReport;
  displayed: number;
  total: number;
  offset: number;
  truncated: boolean;
  fidelity: FidelityReport;
  conflicts: PropertyValues[];
  several_values: PropertyValues[];
  /** Advisory: predicates where the sheet's value differs from what a linked
   *  entity already holds (both are kept — visible before commit). */
  store_conflicts: {
    subject: string;
    predicate: string;
    sheet_value: string;
    stored_values: string[];
  }[];
  store_conflicts_truncated: boolean;
}

export interface ReconcileCandidate {
  uri: string;
  label: string | null;
  entity_type: string;
  /** The candidate's domain-class labels (what the entity actually is). */
  classes: string[];
  score: number;
  /** `"label"` = EXACT label match (the only kind safe to auto-link);
   *  `"similar"` = semantic vector match; `"partial"` = prefix/contains hit.
   *  Similar and partial candidates always need user confirmation. */
  match_kind: "label" | "similar" | "partial";
  /** Whether the candidate's classes include the class the mapping declares
   *  for this column. Only class-confirmed label matches may auto-link. */
  class_match: boolean;
  /** The graph the candidate lives in (matching searches every graph). */
  graph: string | null;
}

export interface ReconcileItem {
  value: string;
  /** The IRI the importer would mint — the key a chosen target maps from. */
  minted_iri: string;
  columns: string[];
  /** The class the mapping declares for this value's column, if any. */
  expected_class: string | null;
  candidates: ReconcileCandidate[];
}

export interface ReconcileResponse {
  items: ReconcileItem[];
  truncated: boolean;
  /** Distinct values the server probed — the denominator for "K of N linked". */
  searched: number;
}

/** One reconcile decision for a minted entity. */
export type EntityDecision =
  | { action: "link"; uri: string }
  | { action: "create_new" }
  | { action: "ignore" };

/** Reconcile decisions the caller passes to preview/commit, by minted IRI. */
export type EntityDecisions = Record<string, EntityDecision>;

/** Upload a CSV/XLSX for profiling. Raw body upload (the server sniffs the
 *  container), mirroring `startLoadJob` — engineered not to throw. */
export async function profileSpreadsheet(
  file: File,
  idToken: string,
  sheet?: string,
  headerRow?: number,
  dataStartRow?: number,
): Promise<ApiResult<SpreadsheetProfileResponse>> {
  const params = new URLSearchParams();
  // The name picks the reader: .json, .md, .html and .txt go through the
  // tabular reader; .csv and workbooks keep the spreadsheet readers.
  if (file.name) params.set("filename", file.name);
  if (sheet) params.set("sheet", sheet);
  if (headerRow !== undefined) params.set("header_row", String(headerRow));
  if (dataStartRow !== undefined)
    params.set("data_start_row", String(dataStartRow));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const url = `/api/v1/import/spreadsheet/profile${qs}`;
  const sessionId = getSessionId();
  let res: Response;
  try {
    res = await fetchWithRetry503(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": file.type || "application/octet-stream",
        ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
      },
      body: file,
    });
  } catch (e) {
    return apiFailure(0, "network error", e instanceof Error ? e.message : String(e));
  }
  if (!res.ok) {
    let errBody: { error?: string; detail?: string } = {};
    try {
      errBody = (await res.json()) as { error?: string; detail?: string };
    } catch {
      /* non-JSON error body */
    }
    return apiFailure(res.status, errBody.error, errBody.detail ?? undefined);
  }
  try {
    return { ok: true, data: (await res.json()) as SpreadsheetProfileResponse };
  } catch (e) {
    return apiFailure(502, "The server's answer could not be read", e instanceof Error ? e.message : String(e));
  }
}

export async function proposeImportSpec(
  body: { namespace?: string; profile: SheetProfile; rows: SheetRow[] },
  idToken: string,
): Promise<ApiResult<ImportSpec>> {
  return apiFetch<ImportSpec>("/api/v1/import/spreadsheet/propose", idToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function previewImport(
  body: {
    spec: ImportSpec;
    rows: SheetRow[];
    decisions?: EntityDecisions;
    ignored_statements?: string[];
    offset?: number;
  },
  idToken: string,
): Promise<ApiResult<SpreadsheetPreviewResponse>> {
  return apiFetch<SpreadsheetPreviewResponse>(
    "/api/v1/import/spreadsheet/preview",
    idToken,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

/** `includeMinted` marks the user having ASKED to look — the Recheck action —
 *  rather than the automatic pass on the way to the preview. Asked, it probes
 *  columns designated to mint too, whose matches the user would otherwise
 *  never be shown. Unasked, it leaves those alone: a designation already made
 *  is not a question worth putting to someone once per row. */
export async function reconcileImport(
  body: { spec: ImportSpec; rows: SheetRow[]; include_minted?: boolean },
  idToken: string,
): Promise<ApiResult<ReconcileResponse>> {
  return apiFetch<ReconcileResponse>(
    "/api/v1/import/spreadsheet/reconcile",
    idToken,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

/** Commit outcome: the load result plus the wizard's counts, so the success
 *  screen can say what actually happened (a reconciled-only import creates
 *  zero new entities and must not read as a no-op). */
export interface CommitResponse extends LoadResponse {
  rows_in: number;
  rows_skipped: number;
  /** Statements the plan generated (pre-dedup); `statements` (inherited)
   *  is what the engine wrote to the chosen graph. */
  statements_planned: number;
  /** Every graph the commit wrote: the classes it created to the ontology,
   *  a vocabulary's terms to the vocabulary, the rest to the chosen graph. */
  placed?: { graph: string; statements: number }[];
  statements_ignored: number;
  entities_minted: number;
  entities_reconciled: number;
  relations: number;
  tags_applied: number;
  references_dropped: number;
  /** Classes created from a class column by this commit. */
  classes_created?: CreatedClass[];
  /** Terms and records created by this commit because a value matched nothing. */
  references_created?: CreatedReference[];
  /** Codes left out because each names more than one term of its code list. */
  codes_ambiguous?: AmbiguousCode[];
  /** The saved mapping this commit used or saved, when any. */
  template_id?: string;
  /** Why the mapping was not saved; the load itself succeeded. */
  template_error?: string;
}

export async function commitImport(
  body: {
    /** The mapping inline; or name a saved one with `template_id`. */
    spec?: ImportSpec;
    template_id?: string;
    /** Save the mapping under this name once the load succeeds. */
    save_as_template?: {
      name: string;
      source_kind?: TemplateSourceKind;
      columns: string[];
    };
    /** Materialized rows (the wizard's form) — or send `records`, raw JSON. */
    rows?: SheetRow[];
    records?: unknown;
    mode?: LoadMode;
    graph?: string;
    decisions?: EntityDecisions;
    ignored_statements?: string[];
    /** The uploaded workbook's filename — becomes the statements' @source. */
    source_label?: string;
  },
  idToken: string,
): Promise<ApiResult<CommitResponse>> {
  return apiFetch<CommitResponse>(
    "/api/v1/import/spreadsheet/commit",
    idToken,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

// ---------------------------------------------------------------------------
// Document extraction (ad hoc): a raw document starts an owner-scoped job; the
// proposal set is accepted per entity and committed through the load path.
// ---------------------------------------------------------------------------

export interface ExtractedProperty {
  predicate: string;
  value: string;
  confidence: number;
}

export interface ExtractedEntity {
  id: string;
  class: string;
  label: string;
  confidence: number;
  properties: ExtractedProperty[];
  page: number | null;
  section: number | null;
  existing_match: string | null;
}

export interface DocumentRecord {
  iri: string;
  filename: string;
  sha256: string;
  bytes: number;
  retrieved_at: string;
}

export interface ExtractOutcome {
  document: DocumentRecord;
  model: string;
  total_chunks: number;
  total_tokens: number;
  entities: ExtractedEntity[];
}

export interface ExtractJobStatus {
  job_id: string;
  state: "running" | "done" | "failed";
  filename: string;
  bytes: number;
  format: string;
  started_at: string;
  elapsed_ms: number;
  progress: { phase: "queued" | "parsing" | "extracting" | "done" };
  result?: ExtractOutcome;
  http_status?: number;
  error?: string;
  detail?: string;
}

export async function startExtraction(
  file: File,
  idToken: string,
): Promise<ApiResult<{ job_id: string; status_url: string }>> {
  const params = new URLSearchParams();
  params.set("filename", file.name);
  return apiFetch<{ job_id: string; status_url: string }>(
    `/api/v1/import/extract?${params.toString()}`,
    idToken,
    {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    },
  );
}

export async function extractJobStatus(
  jobId: string,
  idToken: string,
): Promise<ApiResult<ExtractJobStatus>> {
  return apiFetch<ExtractJobStatus>(
    `/api/v1/import/extract/jobs/${encodeURIComponent(jobId)}`,
    idToken,
  );
}

export async function listExtractJobs(
  idToken: string,
): Promise<ApiResult<{ jobs: ExtractJobStatus[] }>> {
  return apiFetch<{ jobs: ExtractJobStatus[] }>(
    "/api/v1/import/extract/jobs",
    idToken,
  );
}

/** Tier 1 document index: the document's own facts and the known things it
 *  mentions, written to the catalog with no model. */
export interface DocumentFacts {
  title: string | null;
  author: string | null;
  version: string | null;
  date: string | null;
  modified_at: string | null;
  page_count: number | null;
  format: string;
  /** Fields the document stated as a placeholder ("author: unknown"): shown,
   *  never recorded — a placeholder is not a value and never an identity. */
  stated_as_placeholder: string[];
}

export interface DocumentMention {
  iri: string;
  label: string;
  count: number;
  sections: string[];
}

export interface DocumentIndexResponse extends LoadResponse {
  document: DocumentRecord;
  facts: DocumentFacts;
  mentions: DocumentMention[];
  lexicon_terms: number;
  sections: number;
  statements_planned: number;
}

export async function indexDocument(
  file: File,
  idToken: string,
): Promise<ApiResult<DocumentIndexResponse>> {
  const params = new URLSearchParams();
  params.set("filename", file.name);
  params.set("modified", String(file.lastModified));
  return apiFetch<DocumentIndexResponse>(
    `/api/v1/import/document/index?${params.toString()}`,
    idToken,
    {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    },
  );
}

export interface ExtractCommitResponse extends LoadResponse {
  document: DocumentRecord;
  entities_written: number;
  statements_planned: number;
}

export async function commitExtraction(
  jobId: string,
  body: { accepted_ids: string[]; graph?: string; mode?: LoadMode },
  idToken: string,
): Promise<ApiResult<ExtractCommitResponse>> {
  return apiFetch<ExtractCommitResponse>(
    `/api/v1/import/extract/jobs/${encodeURIComponent(jobId)}/commit`,
    idToken,
    { method: "POST", body: JSON.stringify(body) },
  );
}

// ---------------------------------------------------------------------------
// Folder-scope import (MVP: staging + analyze-only plan)
// ---------------------------------------------------------------------------

export interface FolderSheetRef {
  file: string;
  sheet: string;
}

export interface FolderColumnRef {
  sheet: FolderSheetRef;
  column: string;
}

export interface FolderIdentityCandidate {
  a: FolderColumnRef;
  b: FolderColumnRef;
  overlap: number;
  jaccard: number;
  containment_a_in_b: number;
  containment_b_in_a: number;
}

export interface FolderCorpusProfile {
  identity_candidates: FolderIdentityCandidate[];
  duplicate_tabs: { sheets: FolderSheetRef[]; kind: string }[];
  header_clusters: { canonical: string; members: FolderColumnRef[] }[];
  event_grain: {
    sheet: FolderSheetRef;
    subject_column: string;
    qualifier_columns: string[];
    repeated_subjects: number;
  }[];
  sparse_columns: {
    column: FolderColumnRef;
    non_blank_ratio: number;
    applicability_column: string | null;
    applies_when: string[];
  }[];
  skip_suggestions: { sheet: FolderSheetRef; reason: string }[];
  missing_bridges: {
    world_a: FolderColumnRef;
    world_b: FolderColumnRef;
    description: string;
  }[];
}

export interface FolderSheetPlan {
  sheet: FolderSheetRef;
  disposition: string;
  skip_reason: string | null;
  rows: number;
  /** The row the header was read from, counted from zero. */
  header_row: number;
  /** The first rows of data, for the panel that keeps the sheet in view. */
  first_rows: SheetRow[];
  columns: string[];
  /** Each column with its samples and the kind its values read as. */
  profile: SheetProfile;
  spec: ImportSpec | null;
  /** The saved mapping whose header set this sheet's is, or includes, fitted
   *  to the sheet. The pages open with it; `spec` stays the proposal. */
  saved_mapping?: TemplateMatch;
  /** The sheet's fingerprints: its header set and its rows. */
  shape_hash?: string;
  content_hash?: string;
  /** What the catalog already holds of this sheet. Absent for a sheet never
   *  loaded. `unchanged`: a load in force has these rows. `new_version`:
   *  loads have this header set, none these rows. */
  known?: {
    state: "unchanged" | "new_version";
    loads: { source: string; imported_at: string; loaded_by: string | null; rows: number | null; same_content: boolean }[];
  };
  /** Reader notes for a JSON/Markdown/HTML/text table (values joined, nested kept as text). */
  notes?: string[];
}

/** A link saved between two sheets' header sets, placed on this folder's sheets. */
export interface FolderSavedLink {
  kind: string;
  from: FolderColumnRef;
  to: FolderColumnRef;
  predicate?: string;
  primary?: string;
  via?: string;
}

export type FolderFileKind =
  "sheet" | "graph" | "native" | "document" | "other";

export interface FolderFilePlan {
  relative: string;
  kind: FolderFileKind;
  bytes: number;
  disposition: string;
  note: string;
  content?: "ontology" | "vocabulary" | "data" | null;
  proposed_graph?: string | null;
  duplicate_of?: string | null;
  superseded_by?: string | null;
  /** Statements the analysis parse found (native files) — the expected
   *  number the load report's written count should match. */
  statements?: number | null;
  /** Rules a native file carries, known by content; they load with it. */
  rules?: number | null;
}

export interface FolderPlanResponse {
  session_id: string;
  sheets: FolderSheetPlan[];
  files: FolderFilePlan[];
  corpus: FolderCorpusProfile;
  unreadable: [string, string][];
  /** Links saved between the header sets of sheets that are both here. */
  saved_links?: FolderSavedLink[];
  note: string;
}

export interface FolderUploadResponse {
  staged: { relative: string; bytes: number };
  files: number;
  total_bytes: number;
}

export interface FolderArchiveResponse {
  staged: { relative: string; bytes: number }[];
  files: number;
  total_bytes: number;
  skipped: string[];
}

/** Stage every file of a zip under its path in the archive, so a dataset laid
 *  out in folders (ontology, vocabulary, catalog, data) loads as laid out. */
export async function uploadFolderArchive(
  sessionId: string,
  file: File,
  idToken: string,
): Promise<ApiResult<FolderArchiveResponse>> {
  return apiFetch<FolderArchiveResponse>(
    `/api/v1/import/folder/${encodeURIComponent(sessionId)}/archive`,
    idToken,
    { method: "POST", headers: { "Content-Type": "application/zip" }, body: file },
  );
}

export async function createFolderSession(
  idToken: string,
): Promise<ApiResult<{ session_id: string }>> {
  return apiFetch<{ session_id: string }>(
    "/api/v1/import/folder/session",
    idToken,
    {
      method: "POST",
      body: "{}",
    },
  );
}

export async function uploadFolderFile(
  sessionId: string,
  relativePath: string,
  file: File,
  idToken: string,
): Promise<ApiResult<FolderUploadResponse>> {
  const url = `/api/v1/import/folder/${encodeURIComponent(sessionId)}/file?path=${encodeURIComponent(relativePath)}&modified=${encodeURIComponent(String(file.lastModified))}`;
  const sid = getSessionId();
  let res: Response;
  try {
    res = await fetchWithRetry503(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": file.type || "application/octet-stream",
        ...(sid ? { [SESSION_HEADER]: sid } : {}),
      },
      body: file,
    });
  } catch (e) {
    return apiFailure(0, "network error", e instanceof Error ? e.message : String(e));
  }
  if (!res.ok) {
    let errBody: { error?: string; detail?: string } = {};
    try {
      errBody = (await res.json()) as { error?: string; detail?: string };
    } catch {
      /* non-JSON error body */
    }
    return apiFailure(res.status, errBody.error, errBody.detail ?? undefined);
  }
  try {
    return { ok: true, data: (await res.json()) as FolderUploadResponse };
  } catch (e) {
    return apiFailure(502, "The server's answer could not be read", e instanceof Error ? e.message : String(e));
  }
}

export async function planFolderImport(
  sessionId: string,
  idToken: string,
  namespace?: string,
): Promise<ApiResult<FolderPlanResponse>> {
  return apiFetch<FolderPlanResponse>(
    `/api/v1/import/folder/${encodeURIComponent(sessionId)}/plan`,
    idToken,
    { method: "POST", body: JSON.stringify(namespace ? { namespace } : {}) },
  );
}

export interface FolderCommitItem {
  item: string;
  ok: boolean;
  detail: string;
  /** Statements the engine wrote for this item (absent where the backend
   *  cannot count). */
  statements?: number | null;
  /** Held values a later value replaced; absent when none was. */
  updated?: number;
  /** Values not written because the store holds a later one; absent when
   *  none was. */
  not_applied_older?: number;
}

/** One target graph's statement total before the batch first touched it and
 *  after the last load — the report's proof of what actually landed. */
export interface FolderCommitGraphTotal {
  graph: string;
  before: number | null;
  after: number | null;
}

export interface FolderCommitResponse {
  items: FolderCommitItem[];
  loaded: number;
  failed: number;
  graphs: FolderCommitGraphTotal[];
  note: string;
}

/** Batch commit — the folder fast path: every selected file and sheet in one
 *  motion, schema-first, per-item partial failure. Mirrors
 *  `handlers::import_folder::commit_all`. */
/** 202 + a job id: the batch runs server-side; poll [`getFolderBatchJob`]. */
export interface FolderBatchAccepted {
  job_id: string;
  status_url: string;
}

/** Mid-batch progress: finished items plus the file currently loading. */
export interface FolderBatchSnapshot {
  total: number;
  done: number;
  current?: string;
  items: FolderCommitItem[];
}

export interface FolderBatchStatus {
  job_id: string;
  state: "running" | "done" | "failed";
  session: string;
  selected: number;
  started_at: string;
  elapsed_ms: number;
  progress: FolderBatchSnapshot;
  result?: FolderCommitResponse;
  http_status?: number;
  error?: string;
  detail?: string;
}

/** What one sheet of a folder comes to on Validate: `joined` counts the
 *  things found in the store, or in the sheets before it, under an
 *  identifier they carry. */
export interface FolderSheetCounts {
  rows_in: number;
  rows_skipped: number;
  entities_minted: number;
  entities_reconciled: number;
  statements: number;
  references_dropped: number;
  joined: number;
}

export type FolderPreviewSheet = SpreadsheetPreviewResponse & {
  sheet: FolderSheetRef;
  header_row: number;
  counts: FolderSheetCounts;
};

export interface FolderPreviewResponse {
  sheets: FolderPreviewSheet[];
  /** Sheets that could not be read or planned, with why. */
  failed: { sheet: FolderSheetRef; error: string }[];
  links_applied: unknown;
}

/** Validate over the whole folder: each sheet planned after the ones before
 *  it, a thing one sheet creates found by the next through the identifiers
 *  it carries. Nothing is written. */
export async function previewFolder(
  sessionId: string,
  body: {
    sheets: { relative: string; sheet: string; spec: ImportSpec; header_row?: number }[];
    links: unknown;
    decisions: EntityDecisions;
  },
  idToken: string,
): Promise<ApiResult<FolderPreviewResponse>> {
  return apiFetch<FolderPreviewResponse>(
    `/api/v1/import/folder/${encodeURIComponent(sessionId)}/preview`,
    idToken,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function commitFolder(
  sessionId: string,
  files: {
    relative: string;
    graph: string;
    mode: LoadMode;
    action?: "load" | "index";
  }[],
  sheets: {
    relative: string;
    sheet: string;
    spec: ImportSpec;
    header_row?: number;
    graph?: string;
    mode?: LoadMode;
    mapping_id?: string;
    mapping_name?: string;
    /** Remove each earlier load of this sheet once this one is written. */
    replace_earlier?: boolean;
  }[],
  idToken: string,
): Promise<ApiResult<FolderBatchAccepted>> {
  return apiFetch<FolderBatchAccepted>(
    `/api/v1/import/folder/${encodeURIComponent(sessionId)}/commit`,
    idToken,
    { method: "POST", body: JSON.stringify({ files, sheets }) },
  );
}

/** One sheet's mapping as saved from the folder pages. */
export interface FolderSavedMapping {
  relative: string;
  sheet: string;
  id: string;
  name: string;
  /** False when the mapping this header set already had was replaced. */
  created: boolean;
}

/** Save every sheet's mapping, each by its header set, and the links between
 *  the sheets. Nothing is imported. */
export async function saveFolderMappings(
  sessionId: string,
  body: {
    sheets: { relative: string; sheet: string; columns: string[]; spec: ImportSpec }[];
    links: {
      kind: string;
      from: { relative: string; sheet: string; column: string };
      to: { relative: string; sheet: string; column: string };
      predicate?: string;
      primary?: string;
      via?: string;
    }[];
  },
  idToken: string,
): Promise<ApiResult<{ saved: FolderSavedMapping[]; links: number }>> {
  return apiFetch<{ saved: FolderSavedMapping[]; links: number }>(
    `/api/v1/import/folder/${encodeURIComponent(sessionId)}/mappings`,
    idToken,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function getFolderBatchJob(
  jobId: string,
  idToken: string,
): Promise<ApiResult<FolderBatchStatus>> {
  return apiFetch<FolderBatchStatus>(
    `/api/v1/import/folder/jobs/${encodeURIComponent(jobId)}`,
    idToken,
  );
}

export async function deleteFolderSession(
  sessionId: string,
  idToken: string,
): Promise<ApiResult<{ deleted: string }>> {
  return apiFetch<{ deleted: string }>(
    `/api/v1/import/folder/${encodeURIComponent(sessionId)}`,
    idToken,
    { method: "DELETE" },
  );
}

/** A named graph in the store. Mirrors `handlers::graphs::GraphInfo`. */
export interface GraphInfo {
  /** Bracketed canonical IRI (`<urn:tres:data>`). */
  uri: string;
  /** Bare IRI (`urn:tres:data`) — what writes/queries key on. */
  bare: string;
  /** "ontology" / "vocabulary" / "data" / "catalog" for managed graphs, else null. */
  kind: string | null;
  managed: boolean;
  /** Statements in this graph. Absent when the count could not be taken. */
  statements?: number;
}

export async function listGraphs(
  idToken: string,
): Promise<ApiResult<{ graphs: GraphInfo[] }>> {
  return apiFetch<{ graphs: GraphInfo[] }>("/api/v1/graphs", idToken, {
    method: "GET",
  });
}

// ---------------------------------------------------------------------------
// RDF spreadsheet import (direct port of the desktop wizard engine).
//
// The mapping wire format IS the desktop `SpreadsheetTemplate`; these types
// mirror its serde shape (externally-tagged Rust enums → string | { Variant:
// {...} }). The cloud wizard authors `entity_groups` directly — the engine
// reads property mappings from each group's `predicate_object_maps`.
// ---------------------------------------------------------------------------

export type RdfStringTransform =
  | "Trim"
  | "Lowercase"
  | "Uppercase"
  | "UrlEncode"
  | { Replace: { from: string; to: string } }
  | { RegexReplace: { pattern: string; replacement: string } };

export type RdfIdStrategy =
  | "GeneratedUuid"
  | { FromColumn: { column: string; transforms: RdfStringTransform[] } };

export type RdfObjectMap =
  | { Literal: { datatype: string | null; lang: string | null } }
  | "TypedNumber"
  | { TypedDate: { format: string | null } }
  | "TypedBoolean"
  | { IriConstant: { uri_pattern: string; transforms: RdfStringTransform[] } };

export type RdfKeyResolution =
  "Create" | "MatchById" | { MatchByLabel: { label_column: string } };

export type RdfSkosAttachment =
  { TopOfScheme: { scheme_uri: string } } | { ChildOf: { parent_uri: string } };

export interface RdfSubjectMap {
  class_uri: string;
  uri_pattern: string;
  id_strategy: RdfIdStrategy;
}

export interface RdfPredicateObjectMap {
  column_header: string;
  header_synonyms: string[];
  predicate_uri: string;
  object_map: RdfObjectMap;
  required: boolean;
  role: "Data";
}

export interface RdfEntityGroup {
  id: string;
  subject_map: RdfSubjectMap;
  key_column: string;
  key_resolution: RdfKeyResolution;
  predicate_object_maps: RdfPredicateObjectMap[];
  ordinal: number;
  link_predicate: string;
  also_as_property: RdfPredicateObjectMap | null;
  skos_attach: RdfSkosAttachment | null;
}

export interface RdfSpreadsheetTemplate {
  id: string;
  name: string;
  subject_map: RdfSubjectMap;
  predicate_object_maps: RdfPredicateObjectMap[];
  target_graph_uri: string | null;
  upsert_key: "Uri";
  unmapped_column_strategy: "Skip";
  total_predicate: string | null;
  created: string;
  updated: string;
  entity_groups: RdfEntityGroup[] | null;
  column_assignments: null;
  base_instance_uri: string | null;
}

export interface RdfProfileResponse {
  columns: string[];
  rows: string[][];
  sheet_names: string[];
  selected_sheet: string | null;
}

export type RdfUserChoice = "CreateNew" | { UseExisting: string };

export interface RdfMatchCandidate {
  uri: string;
  label: string;
}

export interface RdfAmbiguousMatch {
  key_value: string;
  tentative_uri: string;
  candidates: RdfMatchCandidate[];
  user_choice: RdfUserChoice | null;
}

export interface RdfEntityGroupReport {
  entity_group_id: string;
  class_uri: string;
  class_display: string;
  resolution: RdfKeyResolution;
  total_unique_keys: number;
  unambiguous: {
    key_value: string;
    tentative_uri: string;
    resolved_uri: string;
    label: string;
  }[];
  will_create_count: number;
  ambiguous: RdfAmbiguousMatch[];
}

export interface RdfReconciliationReport {
  groups: RdfEntityGroupReport[];
}

export interface RdfPreviewResponse {
  new_instances: number;
  updated_instances: number;
  skipped_rows: number;
  total_triples: number;
  warnings: string[];
  validation_errors: { row: number; column: string; message: string }[];
  sample_triples: string[];
  truncated: boolean;
  report: RdfReconciliationReport;
}

export interface RdfCommitResponse {
  rows_imported: number;
  rows_updated: number;
  rows_skipped: number;
  triples_written: number;
  graph: string;
  errors: string[];
}

/** Upload a CSV/XLSX for parsing. Raw body upload, mirroring `profileSpreadsheet`. */
export async function rdfProfileSpreadsheet(
  file: File,
  idToken: string,
  sheet?: string,
): Promise<ApiResult<RdfProfileResponse>> {
  const qs = sheet ? `?sheet=${encodeURIComponent(sheet)}` : "";
  const url = `/api/v1/import/rdf-spreadsheet/profile${qs}`;
  const sessionId = getSessionId();
  let res: Response;
  try {
    res = await fetchWithRetry503(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": file.type || "application/octet-stream",
        ...(sessionId ? { [SESSION_HEADER]: sessionId } : {}),
      },
      body: file,
    });
  } catch (e) {
    return apiFailure(0, "network error", e instanceof Error ? e.message : String(e));
  }
  if (!res.ok) {
    let errBody: { error?: string; detail?: string } = {};
    try {
      errBody = (await res.json()) as { error?: string; detail?: string };
    } catch {
      /* non-JSON error body */
    }
    return apiFailure(res.status, errBody.error, errBody.detail ?? undefined);
  }
  try {
    return { ok: true, data: (await res.json()) as RdfProfileResponse };
  } catch (e) {
    return apiFailure(502, "The server's answer could not be read", e instanceof Error ? e.message : String(e));
  }
}

/** Base-IRI suggestions drawn from the data graph's existing subjects, so new
 *  entities mint into the data's own namespace rather than a placeholder. */
export async function rdfImportNamespaces(
  idToken: string,
): Promise<ApiResult<{ namespaces: string[] }>> {
  return apiFetch<{ namespaces: string[] }>(
    "/api/v1/import/rdf-spreadsheet/namespaces",
    idToken,
  );
}

export async function rdfPreviewImport(
  body: {
    template: RdfSpreadsheetTemplate;
    columns: string[];
    rows: string[][];
  },
  idToken: string,
): Promise<ApiResult<RdfPreviewResponse>> {
  return apiFetch<RdfPreviewResponse>(
    "/api/v1/import/rdf-spreadsheet/preview",
    idToken,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export async function rdfCommitImport(
  body: {
    template: RdfSpreadsheetTemplate;
    columns: string[];
    rows: string[][];
    graph?: string;
    mode?: LoadMode;
    report?: RdfReconciliationReport;
  },
  idToken: string,
): Promise<ApiResult<RdfCommitResponse>> {
  return apiFetch<RdfCommitResponse>(
    "/api/v1/import/rdf-spreadsheet/commit",
    idToken,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

// ---- Gremlin / Cosmos DB graph import (RDF backend) ----

export interface GremlinReport {
  vertices_in: number;
  edges_in: number;
  statements_out: number;
  distinct_predicates: number;
  type_conflicts: number;
  dangling_endpoints: number;
  slug_collisions: number;
}
export interface GremlinPreviewResponse {
  namespace: string;
  triples_total: number;
  sample_triples: string[];
  report: GremlinReport;
}
export interface GremlinCommitResponse {
  triples_written: number;
  graph: string;
  replaced: boolean;
  report: GremlinReport;
}
type GremlinBody = {
  vertices: unknown[];
  edges: unknown[];
  namespace?: string;
  normalize_predicates?: boolean;
  graph?: string;
  mode?: LoadMode;
};

export async function gremlinPreview(
  body: GremlinBody,
  idToken: string,
): Promise<ApiResult<GremlinPreviewResponse>> {
  return apiFetch<GremlinPreviewResponse>(
    "/api/v1/import/gremlin/preview",
    idToken,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export async function gremlinCommit(
  body: GremlinBody,
  idToken: string,
): Promise<ApiResult<GremlinCommitResponse>> {
  return apiFetch<GremlinCommitResponse>(
    "/api/v1/import/gremlin/commit",
    idToken,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

/** Records the signed-in person's acceptance of the Terms of Use at `version`. */
export function acceptTerms(
  version: string,
  idToken: string,
): Promise<ApiResult<void>> {
  return apiFetch("/api/v1/me/terms", idToken, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ version }),
  });
}

// ---------------------------------------------------------------------------
// Property-graph import (Neo4j over its HTTP endpoint).
//
// Survey, then preview, then commit the preview. The preview KEEPS what it
// read on the server, so committing does not read the source database twice
// and a plan is committed once (checklist 90.6).
//
// Credentials are sent per call and are not stored: a saved connection whose
// credential lives in the write-only secret store is still owed (90.3).
// ---------------------------------------------------------------------------

export interface LpgConnection {
  url: string;
  database: string;
  username?: string | undefined;
  password?: string | undefined;
}

export interface LpgLabelCount {
  label: string;
  nodes: number;
}
export interface LpgTypeCount {
  relationship_type: string;
  relationships: number;
}
export interface LpgPropertyKey {
  owner: string;
  key: string;
  types: string[];
  mandatory: boolean;
}
export interface LpgSurvey {
  node_labels: LpgLabelCount[];
  relationship_types: LpgTypeCount[];
  node_properties: LpgPropertyKey[];
  relationship_properties: LpgPropertyKey[];
  nodes_total: number;
  relationships_total: number;
}

/** A problem the source posed, and what the import will do about it. */
export interface LpgFinding {
  kind: string;
  detail: string;
  resolution: string;
}

export interface LpgPreviewResponse {
  plan_id: string;
  nodes_read: number;
  relationships_read: number;
  nodes_not_read: number;
  relationships_not_read: number;
  statements: number;
  value_statements: number;
  relationship_statements: number;
  tags: number;
  folded: number;
  distinct_predicates: number;
  findings: LpgFinding[];
  sample: string[];
}

export interface LpgCommitResponse {
  graph: string;
  replaced: boolean;
  /** What the engine confirmed it wrote, which is the number that counts. */
  statements_written: number | null;
  statements_planned: number;
  value_statements: number;
  relationship_statements: number;
  tags: number;
  elapsed_ms: number;
  source: string;
}

export async function lpgSurvey(
  body: LpgConnection,
  idToken: string,
): Promise<ApiResult<LpgSurvey>> {
  return apiFetch<LpgSurvey>("/api/v1/import/lpg/survey", idToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function lpgPreview(
  body: LpgConnection & {
    key?: string | undefined;
    namespace?: string | undefined;
    normalize_predicates?: boolean;
  },
  idToken: string,
): Promise<ApiResult<LpgPreviewResponse>> {
  return apiFetch<LpgPreviewResponse>("/api/v1/import/lpg/preview", idToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function lpgCommit(
  body: { plan_id: string; graph: string; mode: LoadMode },
  idToken: string,
): Promise<ApiResult<LpgCommitResponse>> {
  return apiFetch<LpgCommitResponse>("/api/v1/import/lpg/commit", idToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Databases (36.12): the stores a site holds, one index each.

export interface DatabaseInfo {
  id: string;
  name: string;
  default: boolean;
  backend: string;
  /** The roles that may use it besides the Owner and the Admin; empty for every role. */
  allowed_roles: string[];
}

/** The roles a database can be limited to, as the server names them. */
export const DATABASE_SCOPABLE_ROLES: { role: string; label: string }[] = [
  { role: "PowerUser", label: "Power User" },
  { role: "User", label: "User" },
  { role: "Auditor", label: "Auditor" },
  { role: "Integration", label: "Machine keys" },
  { role: "Anonymous", label: "Guests" },
];

export function setDatabaseRoles(
  id: string,
  allowedRoles: string[],
  accessToken: string,
): Promise<ApiResult<{ id: string; allowed_roles: string[] }>> {
  return apiFetch(`/api/v1/admin/databases/${encodeURIComponent(id)}/roles`, accessToken, {
    method: "PUT",
    body: JSON.stringify({ allowed_roles: allowedRoles }),
  });
}

export interface DatabaseList {
  databases: DatabaseInfo[];
  default: string;
  can_create: boolean;
  max_databases: number;
}

export function listDatabases(
  accessToken: string | undefined,
): Promise<ApiResult<DatabaseList>> {
  return apiFetch("/api/v1/databases", accessToken);
}

export function createDatabase(
  body: { id: string; name?: string },
  accessToken: string,
): Promise<ApiResult<DatabaseInfo>> {
  return apiFetch("/api/v1/admin/databases", accessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function renameDatabase(
  id: string,
  name: string,
  accessToken: string,
): Promise<ApiResult<{ id: string; name: string }>> {
  return apiFetch(`/api/v1/admin/databases/${encodeURIComponent(id)}`, accessToken, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export function deleteDatabase(
  id: string,
  accessToken: string,
): Promise<ApiResult<{ id: string; statements: number; removed: string[] }>> {
  return apiFetch(`/api/v1/admin/databases/${encodeURIComponent(id)}`, accessToken, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Snapshot (36.12.6): the database picked as one zip, and its restore.

/** A snapshot's parts, as the download names them: `graphs`, `prefixes`,
 *  `rules`, `corrections`, and each site table by its name. */
export type SnapshotPart = string;

/** Take a snapshot of a database, any database on the site, not only the one
 *  in use: the parts asked for (every part when none are named) under the name
 *  given. Hands back the file and its notes; where it is saved is the caller's. */
export async function fetchSnapshot(
  accessToken: string,
  databaseId: string,
  options: { include?: SnapshotPart[]; name?: string } = {},
): Promise<
  | { ok: true; blob: Blob; filename: string; warningCount: number; warnings: string[] }
  | ApiFailure
> {
  const params = new URLSearchParams();
  if (options.include) params.set("include", options.include.join(","));
  if (options.name?.trim()) params.set("name", options.name.trim());
  // The files inside carry the time on the taker's clock.
  params.set("utc_offset_minutes", String(-new Date().getTimezoneOffset()));
  const query = params.toString();
  const res = await fetchWithRetry503(`/api/v1/admin/snapshot${query ? `?${query}` : ""}`, {
    headers: { ...authHeaders(accessToken), [DATABASE_HEADER]: databaseId },
  });
  if (!res.ok) return failureFromResponse(res);
  const blob = await res.blob();
  const day = new Date().toISOString().slice(0, 10);
  return {
    ok: true,
    blob,
    filename:
      filenameFromDisposition(res.headers.get("content-disposition")) ??
      `${(options.name?.trim() || `${databaseId}-snapshot-${day}`).replace(/[\\/:*?"<>|]+/g, "-")}.zip`,
    warningCount: Number(res.headers.get("x-tres-snapshot-warning-count") ?? "0") || 0,
    warnings: decodeSnapshotWarnings(res.headers.get("x-tres-snapshot-warnings")),
  };
}

/** The notes a snapshot download carries in its header: base64 of a JSON
 *  list of lines. Anything unreadable reads as no lines; the count header
 *  still says how many the manifest holds. */
export function decodeSnapshotWarnings(header: string | null): string[] {
  if (!header) return [];
  try {
    const bytes = Uint8Array.from(atob(header), (c) => c.charCodeAt(0));
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return Array.isArray(parsed) ? parsed.filter((l): l is string => typeof l === "string") : [];
  } catch {
    return [];
  }
}

/** Restore a snapshot into the database picked; the answer is a load job. */
export interface SnapshotContents {
  database: string;
  database_name: string;
  graphs: number;
  statements: number;
  prefixes: number;
  rules: number;
  rules_off: number;
  corrections: number;
  /** Each site table with rows, and the tables its rows refer to. */
  state: { table: string; label: string; rows: number; parents?: string[] }[];
}

/** What a snapshot of a database would hold, as counts: the one named, or the
 *  database in use. */
export async function fetchSnapshotContents(
  accessToken: string,
  databaseId?: string,
): Promise<ApiResult<SnapshotContents>> {
  return apiFetch<SnapshotContents>(
    "/api/v1/admin/snapshot/contents",
    accessToken,
    databaseId ? { headers: { [DATABASE_HEADER]: databaseId } } : {},
  );
}

export async function restoreSnapshot(
  file: File,
  accessToken: string,
): Promise<ApiResult<{ job_id: string; status_url: string }>> {
  return apiFetch("/api/v1/admin/snapshot", accessToken, {
    method: "POST",
    headers: { "Content-Type": "application/zip" },
    body: file,
  });
}
